import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, IconControl, SearchInput } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import FieldRow from "./FieldRow";
import MappingOverlay from "./MappingOverlay";
import EntityFieldEditDialog from "./EntityFieldEditDialog";
import EntityObjectEditDialog from "./EntityObjectEditDialog";
import TransformToolkitDialog from "./TransformToolkitDialog";
import AiMappingConfirmDialog from "./AiMappingConfirmDialog";
import {
  asArray,
  buildEmptyFieldMap,
  buildEmptyLoopMap,
  draftKey,
  EMPTY_GUID,
  LOOP_AGGREGATE_REDUCERS,
  LOOP_SOURCE_MODES,
  LOOP_TARGET_MODES,
  getFieldDataTypeLabel as getSharedFieldDataTypeLabel,
  mapDiff,
  normalizeField,
  ensureUniqueMapItemIds,
  rehydrateDraftMap,
  replaceMapping,
  searchFieldMatch,
  toDraftMap,
  TRANSFORM_KIND_CUSTOM,
  TRANSFORM_KIND_ENTITY_LOOKUP,
  TRANSFORM_KIND_MATH,
  TRANSFORM_KIND_SOURCE_LOOKUP,
} from "./utils";
import "./entityFieldMapDesigner.scss";

const cloneMap = (map) => ({
  ...map,
  items: asArray(map?.items).map((x) => ({ ...x })),
});

const cloneFields = (fields) => asArray(fields).map((field) => ({ ...field }));
const cloneLoops = (loops) => asArray(loops).map((loop) => ({
  ...loop,
  aggregates: asArray(loop?.aggregates).map((aggregate) => ({
    ...aggregate,
    transformFields: asArray(aggregate?.transformFields).map((input) => ({ ...input })),
    preTransformSteps: asArray(aggregate?.preTransformSteps).map((step) => ({ ...step })),
    transformSteps: asArray(aggregate?.transformSteps).map((step) => ({ ...step })),
  })),
}));

const loopDraftKey = (entityObjectId, sourceObjectId) => `${draftKey(entityObjectId, sourceObjectId, "loops")}`;

const getEffectiveLoopSourceMode = (loop) => {
  const rawMode = loop?.loopSourceMode || "GroupedRows";
  if (
    rawMode === "GroupedRows" &&
    (loop?.collectionSourceFieldId || String(loop?.collectionPath || "").trim()) &&
    !loop?.groupBySourceFieldId
  ) {
    return "InlineCollection";
  }
  return rawMode;
};

const normalizeLoopSourceMode = (loop) => {
  if (!loop) return loop;
  const sourceMode = getEffectiveLoopSourceMode(loop);
  if (sourceMode === (loop?.loopSourceMode || "GroupedRows")) return loop;
  return {
    ...loop,
    loopSourceMode: sourceMode,
    groupBySourceFieldId: null,
    sortSourceFieldId: null,
  };
};

const normalizeLoopSourceModes = (loops) => asArray(loops).map((loop) => normalizeLoopSourceMode(loop));

const sameIdOrder = (left, right) => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};

const reorderByIdList = (fields, idOrder) => {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const ordered = idOrder.map((id) => byId.get(id)).filter(Boolean);
  const used = new Set(ordered.map((field) => field.id));
  const extras = fields.filter((field) => !used.has(field.id));
  return [...ordered, ...extras];
};

const typeKey = (field) => String(field?.dataTypeId || field?.dataTypeName || "").toLowerCase();
const normalizeTypeKey = (field) => typeKey(field).replace(/^system\./, "").replace(/[^a-z0-9]/g, "");
const isNumericType = (field) => {
  const key = normalizeTypeKey(field);
  if (!key) return false;
  return [
    "byte",
    "sbyte",
    "short",
    "ushort",
    "int",
    "uint",
    "int16",
    "int32",
    "int64",
    "uint16",
    "uint32",
    "uint64",
    "integer",
    "long",
    "single",
    "float",
    "double",
    "decimal",
    "number",
    "numeric",
    "currency",
    "money",
  ].some((token) => key === token || key.endsWith(token));
};
const getFieldValidationLabel = (field, fallback = "Input") => {
  const label = String(field?.displayName || field?.fieldName || field?.id || fallback || "").trim();
  const dataType = getFieldDataTypeLabel(field);
  return dataType ? `${label} (${dataType})` : label;
};
const isDeletedEdge = (edge) => edge?.isDeleted === true;
const isActiveEdge = (edge) => edge?.isActive !== false && !isDeletedEdge(edge);
const hasRenderableSource = (edge, sourceFieldById) =>
  edge?.isConstant === true || edge?.sourceFieldId === EMPTY_GUID || sourceFieldById.has(edge?.sourceFieldId);
const hasRenderableEntity = (edge, entityFieldById) => !!edge?.entityFieldId && entityFieldById.has(edge.entityFieldId);
const MATH_OPERATORS_REQUIRING_OPERAND = new Set(["Add", "Subtract", "Multiply", "Divide", "Modulo", "Min", "Max", "Coalesce"]);
const MATH_OPERATORS_REQUIRING_SCALE = new Set(["Round", "ImpliedDecimal"]);
const SUPPORTED_MATH_OPERATORS = new Set(["Add", "Subtract", "Multiply", "Divide", "Modulo", "Round", "Ceiling", "Floor", "Absolute", "Min", "Max", "Coalesce", "Percent", "ImpliedDecimal"]);
const SUPPORTED_LOOP_SOURCE_MODES = new Set(LOOP_SOURCE_MODES.map((option) => option.Value));
const SUPPORTED_LOOP_TARGET_MODES = new Set(LOOP_TARGET_MODES.map((option) => option.Value));

const buildMappingSearchLabel = (edge, sourceFieldById, entityFieldById) => {
  const sourceField = edge?.isConstant ? null : sourceFieldById.get(edge?.sourceFieldId);
  const entityField = entityFieldById.get(edge?.entityFieldId);
  const sourceLabel = edge?.isConstant
    ? "(Constant)"
    : (sourceField?.displayName || sourceField?.fieldName || edge?.sourceFieldId || "Unknown Source");
  const entityLabel = entityField?.displayName || entityField?.fieldName || edge?.entityFieldId || "Unknown Entity";
  const stateLabel = isActiveEdge(edge) ? "Active" : "Inactive";
  return `${sourceLabel} -> ${entityLabel} [${stateLabel}]`;
};

const buildSearchSelectMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SearchSelectList,
  DataJson: dataJson,
  ...(placeHolder === null || placeHolder === undefined ? {} : { PlaceHolder: placeHolder }),
});

const buildSelectListMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataJson: dataJson,
  ...(placeHolder === null || placeHolder === undefined ? {} : { PlaceHolder: placeHolder }),
});

const buildTextBoxMetadata = ({ fieldName, displayName, placeHolder = "" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.TextBox,
  PlaceHolder: placeHolder,
});

const buildCheckBoxMetadata = ({ fieldName, displayName }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.CheckBox,
});

const toFieldControlOptions = (options) =>
  asArray(options).map((option) => ({
    Value: option?.Value ?? option?.value ?? "",
    Text: option?.Text ?? option?.label ?? "",
  }));

const getCaseInsensitiveValue = (object, names) => {
  if (!object || typeof object !== "object") return undefined;
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(object, name)) return object[name];
  }
  const keys = Object.keys(object);
  for (const name of names) {
    const match = keys.find((key) => String(key).toLowerCase() === String(name).toLowerCase());
    if (match) return object[match];
  }
  return undefined;
};

const readBoolMetadata = (object, names) => {
  const value = getCaseInsensitiveValue(object, names);
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(normalized)) return true;
  if (["false", "0", "no", "n"].includes(normalized)) return false;
  return null;
};

const hasCollectionTypeHint = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === "100" || normalized === "subitem" || normalized === "subgrid" || normalized === "grid") return true;
  if (normalized.includes("collectioncontainer") || normalized.includes("collectionreference")) return true;
  if (/collection\s*\(/i.test(normalized)) return true;
  if (/\[\]\s*$/.test(normalized)) return true;
  return /(^|[^a-z])(array|jarray|jsonarray|list|ilist|ienumerable|icollection|ireadonlycollection)([^a-z]|$)/i.test(normalized);
};

const isSourceCollectionField = (field) => {
  const raw = field?.raw || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  const collectionFlag =
    readBoolMetadata(raw, ["IsCollection", "SourceFieldIsCollection", "FieldIsCollection"]) ??
    readBoolMetadata(customFields, [
      "IsCollection",
      "SourceFieldIsCollection",
      "FieldIsCollection",
      "OdooIsCollection",
      "BusinessCentralIsCollection",
      "NetSuiteIsCollection",
      "GreatPlainsIsCollection",
    ]);

  if (collectionFlag !== null) return collectionFlag;

  if (
    hasChildFieldDefinitions(raw?.SourceObjectFields) ||
    hasChildFieldDefinitions(raw?.sourceObjectFields) ||
    hasChildFieldDefinitions(raw?.EntityObjectFields) ||
    hasChildFieldDefinitions(raw?.entityObjectFields) ||
    hasChildFieldDefinitions(raw?.Fields) ||
    hasChildFieldDefinitions(raw?.fields) ||
    hasChildFieldDefinitions(getCaseInsensitiveValue(customFields, ["SourceObjectFields", "EntityObjectFields", "ChildFields", "Fields"]))
  ) {
    return true;
  }

  return [
    field?.dataTypeName,
    field?.dataTypeId,
    field?.format,
    raw?.DataTypeName,
    raw?.DataTypeId,
    raw?.SystemDataType,
    raw?.Format,
    raw?.FieldType,
    getCaseInsensitiveValue(customFields, ["FieldType", "OdooType", "BusinessCentralFieldType", "SourceFieldType"]),
    getCaseInsensitiveValue(customFields, ["FieldShape", "OdooFieldShape", "BusinessCentralFieldShape"]),
  ].some(hasCollectionTypeHint);
};

const getCollectionPathForField = (field) => {
  const raw = field?.raw || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  return String(
    getCaseInsensitiveValue(raw, ["CollectionPath", "FieldPath", "SourcePath", "Path", "JsonPath"]) ||
      getCaseInsensitiveValue(customFields, ["CollectionPath", "FieldPath", "SourcePath", "Path", "JsonPath"]) ||
      field?.fieldName ||
      raw?.FieldName ||
      raw?.Name ||
      ""
  ).trim();
};

const hasChildFieldDefinitions = (value) => asArray(value).some((field) => field && typeof field === "object");

const getFieldArrayFromCandidate = (value) => {
  if (hasChildFieldDefinitions(value)) return asArray(value);
  if (!value || typeof value !== "object") return [];
  const direct = getCaseInsensitiveValue(value, [
    "EntityObjectFields",
    "SourceObjectFields",
    "ChildFields",
    "Fields",
    "FieldDefinitions",
    "Columns",
    "Items",
  ]);
  if (hasChildFieldDefinitions(direct)) return asArray(direct);
  return [];
};

const getRawFieldIdentityValues = (field) => {
  const raw = field?.raw || field || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  const normalized = field?.raw ? field : normalizeField(raw);
  return [
    normalized?.id,
    normalized?.fieldName,
    normalized?.displayName,
    raw?.id,
    raw?._id,
    raw?.Id,
    raw?.ID,
    raw?.UniqueId,
    raw?.FieldId,
    raw?.SourceObjectFieldId,
    raw?.EntityObjectFieldId,
    raw?.FieldName,
    raw?.Name,
    raw?.DisplayName,
    getCaseInsensitiveValue(customFields, ["FieldId", "SourceObjectFieldId", "EntityObjectFieldId", "FieldName", "Name", "DisplayName"]),
  ]
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
};

const rawFieldMatchesField = (rawField, field) => {
  const rawValues = new Set(getRawFieldIdentityValues(rawField));
  return getRawFieldIdentityValues(field).some((value) => rawValues.has(value));
};

const rawFieldBelongsToCollection = (rawField, collectionField) => {
  if (!rawField || !collectionField || rawFieldMatchesField(rawField, collectionField)) return false;
  const raw = rawField?.raw || rawField || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  const parentValue = String(
    getCaseInsensitiveValue(raw, [
      "CollectionParentFieldId",
      "CollectionFieldId",
      "ParentFieldId",
      "ParentEntityObjectFieldId",
      "ParentSourceObjectFieldId",
      "ParentFieldName",
      "CollectionParentFieldName",
      "CollectionName",
    ]) ||
      getCaseInsensitiveValue(customFields, [
        "CollectionParentFieldId",
        "CollectionFieldId",
        "ParentFieldId",
        "ParentEntityObjectFieldId",
        "ParentSourceObjectFieldId",
        "ParentFieldName",
        "CollectionParentFieldName",
        "CollectionName",
      ]) ||
      ""
  )
    .trim()
    .toLowerCase();
  const collectionIds = getRawFieldIdentityValues(collectionField);
  if (parentValue && collectionIds.includes(parentValue)) return true;

  const fieldPath = String(
    getCaseInsensitiveValue(raw, ["CollectionPath", "FieldPath", "SourcePath", "Path", "JsonPath"]) ||
      getCaseInsensitiveValue(customFields, ["CollectionPath", "FieldPath", "SourcePath", "Path", "JsonPath"]) ||
      ""
  )
    .trim()
    .toLowerCase();
  const collectionPath = String(getCollectionPathForField(collectionField) || "").trim().toLowerCase();
  return !!fieldPath && !!collectionPath && fieldPath !== collectionPath && fieldPath.startsWith(`${collectionPath}.`);
};

const getScopedChildRawFields = (collectionField, rawFields) =>
  asArray(rawFields).filter((rawField) => rawFieldBelongsToCollection(rawField, collectionField));

const getCollectionChildRawFields = (field, side) => {
  const raw = field?.raw || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  const candidates = side === "entity"
    ? [
        raw?.EntityObjectFields,
        raw?.entityObjectFields,
        raw?.ChildFields,
        raw?.childFields,
        raw?.Fields,
        raw?.fields,
        getCaseInsensitiveValue(customFields, ["EntityObjectFields", "ChildFields", "Fields", "FieldDefinitions", "Columns"]),
        raw?.EntityObject,
        raw?.entityObject,
        raw?.ChildEntityObject,
        raw?.childEntityObject,
        raw?.SubGrid,
        raw?.subGrid,
        raw?.CollectionObject,
        raw?.collectionObject,
        raw?.SourceObjectFields,
        raw?.sourceObjectFields,
        getCaseInsensitiveValue(customFields, ["SourceObjectFields"]),
      ]
    : [
        raw?.SourceObjectFields,
        raw?.sourceObjectFields,
        raw?.ChildFields,
        raw?.childFields,
        raw?.Fields,
        raw?.fields,
        getCaseInsensitiveValue(customFields, ["SourceObjectFields", "ChildFields", "Fields", "FieldDefinitions", "Columns"]),
        raw?.SourceObject,
        raw?.sourceObject,
        raw?.ChildSourceObject,
        raw?.childSourceObject,
        raw?.SubObject,
        raw?.subObject,
        raw?.CollectionObject,
        raw?.collectionObject,
        raw?.SubGrid,
        raw?.subGrid,
        raw?.EntityObjectFields,
        raw?.entityObjectFields,
        getCaseInsensitiveValue(customFields, ["EntityObjectFields"]),
      ];
  for (const candidate of candidates) {
    const fields = getFieldArrayFromCandidate(candidate);
    if (fields.length > 0) {
      const scopedFields = getScopedChildRawFields(field, fields);
      if (scopedFields.length > 0) return scopedFields;
      if (fields.some((candidateField) => rawFieldMatchesField(candidateField, field))) continue;
      return fields;
    }
  }
  return [];
};

const normalizeCollectionChildField = (parentField, childRaw, index) => {
  const normalized = normalizeField(childRaw || {});
  const fallbackName = childRaw?.FieldName || childRaw?.Name || childRaw?.DisplayName || childRaw?.SystemName || `Field ${index + 1}`;
  const parentId = String(parentField?.id || parentField?.fieldName || "collection").trim();
  const childKey = String(normalized.id || fallbackName || index).trim().replace(/\s+/g, "_");
  const parentPath = getCollectionPathForField(parentField) || parentField?.fieldName || parentField?.displayName || "";
  return {
    ...normalized,
    id: normalized.id || `${parentId}::${childKey || index}`,
    fieldName: normalized.fieldName || fallbackName,
    displayName: normalized.displayName || fallbackName,
    collectionParentFieldId: parentField?.id || null,
    collectionParentDisplayName: parentField?.displayName || parentField?.fieldName || "",
    collectionPath: [parentPath, normalized.fieldName || fallbackName].filter(Boolean).join("."),
    raw: {
      ...(childRaw || {}),
      CollectionParentFieldId: parentField?.id || null,
      CollectionParentFieldName: parentField?.fieldName || "",
    },
  };
};

const getCollectionChildFields = (field, side) =>
  getCollectionChildRawFields(field, side)
    .map((childRaw, index) => normalizeCollectionChildField(field, childRaw, index))
    .filter((child) => child?.id && child?.isActive !== false);

const flattenFieldsWithCollectionChildren = (fields, side) =>
  asArray(fields).flatMap((field) => [field, ...getCollectionChildFields(field, side)]);

const getFieldParentCollectionValue = (field) => {
  const raw = field?.raw || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  return String(
    field?.collectionParentFieldId ||
      getCaseInsensitiveValue(raw, [
        "CollectionParentFieldId",
        "CollectionFieldId",
        "ParentFieldId",
        "ParentEntityObjectFieldId",
        "ParentSourceObjectFieldId",
        "ParentFieldName",
        "CollectionParentFieldName",
        "CollectionName",
      ]) ||
      getCaseInsensitiveValue(customFields, [
        "CollectionParentFieldId",
        "CollectionFieldId",
        "ParentFieldId",
        "ParentEntityObjectFieldId",
        "ParentSourceObjectFieldId",
        "ParentFieldName",
        "CollectionParentFieldName",
        "CollectionName",
      ]) ||
      ""
  ).trim();
};

const fieldBelongsToCollection = (field, collectionField) => {
  if (!field || !collectionField || field.id === collectionField.id) return false;
  const parentValue = getFieldParentCollectionValue(field).toLowerCase();
  const collectionIds = [
    collectionField?.id,
    collectionField?.fieldName,
    collectionField?.displayName,
    collectionField?.raw?._id,
    collectionField?.raw?.UniqueId,
    collectionField?.raw?.FieldName,
    collectionField?.raw?.Name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  if (parentValue && collectionIds.includes(parentValue)) return true;

  const raw = field?.raw || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  const fieldPath = String(
    field?.collectionPath ||
      getCaseInsensitiveValue(raw, ["CollectionPath", "FieldPath", "SourcePath", "Path", "JsonPath"]) ||
      getCaseInsensitiveValue(customFields, ["CollectionPath", "FieldPath", "SourcePath", "Path", "JsonPath"]) ||
      ""
  ).trim().toLowerCase();
  const collectionPath = String(getCollectionPathForField(collectionField) || "").trim().toLowerCase();
  return !!fieldPath && !!collectionPath && fieldPath !== collectionPath && fieldPath.startsWith(`${collectionPath}.`);
};

const getCollectionScopedFields = (allFields, collectionField, side) => {
  if (!collectionField) return asArray(allFields);
  const nestedFields = getCollectionChildFields(collectionField, side);
  if (nestedFields.length > 0) {
    const shapeFields = getCollectionShapeFallbackFields(nestedFields, collectionField);
    return shapeFields.length > 0 && collectionFieldsLookMixedWithParent(nestedFields) ? shapeFields : nestedFields;
  }
  const relatedFields = asArray(allFields).filter((field) => fieldBelongsToCollection(field, collectionField));
  if (relatedFields.length > 0) return relatedFields;
  const shapeFields = getCollectionShapeFallbackFields(allFields, collectionField);
  return shapeFields.length > 0 ? shapeFields : asArray(allFields);
};

const getFieldMatchKeys = (field) =>
  [
    field?.fieldName,
    field?.displayName,
    field?.raw?.FieldName,
    field?.raw?.Name,
    field?.raw?.DisplayName,
    field?.raw?.SystemName,
  ]
    .map((value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""))
    .filter(Boolean);

const filterFieldsByTargetCollectionShape = (fields, targetFields, collectionField) => {
  const targetKeys = new Set(asArray(targetFields).flatMap(getFieldMatchKeys));
  if (targetKeys.size === 0) return fields;
  const collectionIds = new Set(getRawFieldIdentityValues(collectionField));
  const matchedFields = asArray(fields).filter((field) => {
    if (getRawFieldIdentityValues(field).some((value) => collectionIds.has(value))) return false;
    return getFieldMatchKeys(field).some((key) => targetKeys.has(key));
  });
  return matchedFields.length > 0 ? matchedFields : fields;
};

const getCollectionShapeFallbackFields = (allFields, collectionField) => {
  const collectionKeys = getFieldMatchKeys(collectionField);
  const isDetailCollection = collectionKeys.some((key) => /lines?|items?|details?|rows?/.test(key));
  if (!isDetailCollection) return [];
  const collectionIds = new Set(getRawFieldIdentityValues(collectionField));
  const detailTokens = [
    "line",
    "item",
    "quantity",
    "qty",
    "uom",
    "unit",
    "unitprice",
    "price",
    "rate",
    "description",
    "sku",
    "product",
    "account",
  ];
  return asArray(allFields).filter((field) => {
    if (getRawFieldIdentityValues(field).some((value) => collectionIds.has(value))) return false;
    const keys = getFieldMatchKeys(field);
    return keys.some((key) => detailTokens.some((token) => key.includes(token)));
  });
};

const collectionFieldsLookMixedWithParent = (fields) => {
  const parentTokens = [
    "systemcreated",
    "interchange",
    "sender",
    "receiver",
    "standardversion",
    "rawfilename",
    "transactionset",
    "groupcontrol",
    "purposecode",
    "ordertype",
    "orderdate",
    "ponumber",
  ];
  return asArray(fields).some((field) => getFieldMatchKeys(field).some((key) => parentTokens.some((token) => key.includes(token))));
};

const isTargetContainerField = (field) => {
  const raw = field?.raw || {};
  const customFields = raw?.CustomFields || raw?.customFields || {};
  const collectionFlag =
    readBoolMetadata(raw, ["IsCollection", "EntityFieldIsCollection", "FieldIsCollection", "IsSubGrid", "IsChildCollection"]) ??
    readBoolMetadata(customFields, [
      "IsCollection",
      "EntityFieldIsCollection",
      "FieldIsCollection",
      "IsSubGrid",
      "IsChildCollection",
      "OdooIsCollection",
      "BusinessCentralIsCollection",
      "NetSuiteIsCollection",
      "GreatPlainsIsCollection",
    ]);

  if (collectionFlag !== null) return collectionFlag;

  if (
    hasChildFieldDefinitions(raw?.EntityObjectFields || raw?.entityObjectFields || raw?.Fields || raw?.fields) ||
    hasChildFieldDefinitions(getCaseInsensitiveValue(customFields, ["EntityObjectFields", "ChildFields", "Fields"]))
  ) {
    return true;
  }

  return [
    field?.dataTypeName,
    field?.dataTypeId,
    field?.format,
    raw?.DataTypeName,
    raw?.DataTypeId,
    raw?.SystemDataType,
    raw?.Format,
    raw?.FieldType,
    raw?.UIFieldType,
    getCaseInsensitiveValue(customFields, ["FieldType", "EntityFieldType", "TargetFieldType"]),
    getCaseInsensitiveValue(customFields, ["FieldShape", "EntityFieldShape", "TargetFieldShape"]),
  ].some((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    return normalized === "subgrid" || normalized === "grid" || normalized === "collectioncontainer" || hasCollectionTypeHint(value);
  });
};

const getEnumText = (items, value, fallback = "") =>
  asArray(items).find((item) => String(item?.Value || "") === String(value || ""))?.Text || fallback || value || "";

const getOptionLabel = (options, value) => {
  const target = String(value || "");
  if (!target) return "";
  return asArray(options).find((option) => String(option?.value || "") === target)?.label || "";
};

const getFieldDataTypeLabel = (field) => getSharedFieldDataTypeLabel(field, "");

const getFieldOptionLabel = (field) => {
  const label = String(field?.displayName || field?.fieldName || field?.id || "").trim();
  const dataType = getFieldDataTypeLabel(field);
  if (!label || !dataType) return label;
  if (label.toLowerCase().includes(`(${dataType.toLowerCase()})`)) return label;
  return `${label} (${dataType})`;
};

const getFieldSummaryLabel = (field, fallbackLabel = "") =>
  String(field?.displayName || field?.fieldName || field?.id || fallbackLabel || "").trim();

const prioritizeFieldById = (fields, priorityFieldId) => {
  const priorityId = String(priorityFieldId || "").trim();
  if (!priorityId) return fields;
  const index = asArray(fields).findIndex((field) => String(field?.id || "") === priorityId);
  if (index <= 0) return fields;
  const nextFields = [...fields];
  const [priorityField] = nextFields.splice(index, 1);
  return [priorityField, ...nextFields];
};

const renderHeaderFieldLabel = (field, fallbackLabel = "Not set") => {
  const displayName = String(field?.displayName || field?.fieldName || field?.id || fallbackLabel || "").trim();
  const dataType = getFieldDataTypeLabel(field);
  if (!dataType) return <span className="efmd-transform-header-field-name">{displayName}</span>;
  return (
    <>
      <span className="efmd-transform-header-field-name">{displayName}</span>{" "}
      <span className="efmd-transform-header-field-type">({dataType})</span>
    </>
  );
};

const getLoopNumberFromAlias = (loop) => {
  const match = String(loop?.loopAlias || "").trim().match(/^loop(\d+)$/i);
  const parsed = match ? Number(match[1]) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const getLoopDefaultName = (number, loopOrSourceMode = null) => {
  const sourceMode = typeof loopOrSourceMode === "string" ? loopOrSourceMode : getEffectiveLoopSourceMode(loopOrSourceMode);
  return sourceMode === "GroupedRows" ? `Group ${number || 1}` : `Loop ${number || 1}`;
};

const isGeneratedLoopDisplayName = (name) => {
  const value = String(name || "").trim().toLowerCase();
  return !value || value === "loop" || value === "new loop" || /^loop\s+\d+$/.test(value) || /^group\s+\d+$/.test(value);
};

const getLoopDisplayName = (loop, number) => {
  const name = String(loop?.name || "").trim();
  if (!isGeneratedLoopDisplayName(name)) return name;
  return getLoopDefaultName(number, loop);
};

const getLoopModeDisplayNumber = (loops, loop, fallbackNumber = 1) => {
  if (!loop) return fallbackNumber || 1;
  const sourceMode = getEffectiveLoopSourceMode(loop);
  let count = 0;
  for (const candidate of asArray(loops).filter((item) => item?.isDeleted !== true && item?.isActive !== false)) {
    if (getEffectiveLoopSourceMode(candidate) === sourceMode) count += 1;
    if (candidate?.id && loop?.id && candidate.id === loop.id) return Math.max(1, count);
  }
  return fallbackNumber || 1;
};

const buildLoopSummary = (loop, loopFieldOptions, entityFieldOptions) => {
  if (!loop) return "";
  const effectiveSourceMode = getEffectiveLoopSourceMode(loop);
  const sourceMode = getEnumText(LOOP_SOURCE_MODES, effectiveSourceMode, "Grouped Rows");
  const collectionField = getOptionLabel(loopFieldOptions, loop.collectionSourceFieldId);
  const groupField = getOptionLabel(loopFieldOptions, loop.groupBySourceFieldId);
  const sortField = getOptionLabel(loopFieldOptions, loop.sortSourceFieldId);
  const targetContainer = effectiveSourceMode === "GroupedRows" ? "" : getOptionLabel(entityFieldOptions, loop.targetContainerEntityObjectFieldId);
  const aggregateCount = asArray(loop.aggregates).filter((aggregate) => aggregate?.isDeleted !== true && aggregate?.isActive !== false).length;
  const parts = [sourceMode, `Output: ${targetContainer ? "Child list" : "Flat rows"}`];
  if (collectionField) parts.push(`Collection: ${collectionField}`);
  else if (String(loop.collectionPath || "").trim()) parts.push(`Path: ${loop.collectionPath}`);
  if (groupField) parts.push(`Group: ${groupField}`);
  if (sortField) parts.push(`Sort: ${sortField}`);
  if (targetContainer) parts.push(`Target: ${targetContainer}`);
  if (aggregateCount > 0) parts.push(`${aggregateCount} aggregate${aggregateCount === 1 ? "" : "s"}`);
  return parts.join(" | ");
};

const makeTransformInputForField = (field, sourceObjectId, sequence = 1) => ({
  id: `input_${Math.random().toString(36).slice(2)}`,
  sourceObjectId,
  sourceFieldId: field?.id || null,
  fieldName: field?.fieldName || field?.displayName || "",
  inputName: field?.fieldName || field?.displayName || "",
  sequence,
  isActive: true,
  isDeleted: false,
});

const compactTransformInputs = (inputs) => {
  const seen = new Set();
  return asArray(inputs)
    .filter((input) => input?.sourceFieldId && input?.isDeleted !== true && input?.isActive !== false)
    .filter((input) => {
      const key = String(input.sourceFieldId);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((input, index) => ({
      ...input,
      sequence: index + 1,
      isActive: true,
      isDeleted: false,
    }));
};

const getCanvasDropTarget = (event) => {
  const target = event?.target;
  const element = target && typeof target.closest === "function" ? target : null;
  const dropElement = element?.closest?.("[data-efmd-map-edge-id], [data-efmd-aggregate-id], [data-efmd-target-entity-id]");
  if (!dropElement) {
    return {
      mapEdgeId: null,
      aggregateId: null,
      targetEntityFieldId: null,
    };
  }
  return {
    mapEdgeId: dropElement.getAttribute("data-efmd-map-edge-id") || null,
    aggregateId: dropElement.getAttribute("data-efmd-aggregate-id") || null,
    targetEntityFieldId: dropElement.getAttribute("data-efmd-target-entity-id") || null,
  };
};

const addSourceInputToMappingItem = (item, sourceField, sourceObjectId, sourceFieldLookup, options = {}) => {
  if (!item || !sourceField?.id) return item;
  const includePrimary = options.includePrimary === true;
  const primarySourceFieldId = item?.sourceFieldId || item?.inputSourceFieldId || null;
  let inputs = asArray(item?.transformFields).map((input) => ({ ...input }));

  if (
    includePrimary &&
    primarySourceFieldId &&
    primarySourceFieldId !== EMPTY_GUID &&
    !inputs.some((input) => input?.sourceFieldId === primarySourceFieldId)
  ) {
    const primaryField = sourceFieldLookup.get(primarySourceFieldId);
    if (primaryField) inputs = [makeTransformInputForField(primaryField, item?.sourceObjectId || sourceObjectId, 1), ...inputs];
  }

  if (!inputs.some((input) => input?.sourceFieldId === sourceField.id) && primarySourceFieldId !== sourceField.id) {
    inputs.push(makeTransformInputForField(sourceField, sourceObjectId, inputs.length + 1));
  }

  return {
    ...item,
    transformFields: compactTransformInputs(inputs),
  };
};

const normalizeTransformStepKind = (step) => {
  const kind = String(step?.kind || "").trim().toLowerCase();
  if (kind) return kind;

  const transformKind = String(step?.transformKind || step?.TransformKind || "").trim();
  if (transformKind === TRANSFORM_KIND_SOURCE_LOOKUP) return "lookup_source";
  if (transformKind === TRANSFORM_KIND_ENTITY_LOOKUP) return "lookup_entity";
  if (transformKind === TRANSFORM_KIND_MATH) return "math";
  if (transformKind === TRANSFORM_KIND_CUSTOM) return "custom";

  const key = String(step?.key || step?.transformKey || step?.TransformKey || "").trim().toLowerCase();
  if (key === "custom") return "custom";
  if (step?.LookupSourceObjectId || step?.lookupSourceObjectId) return "lookup_source";
  if (step?.LookupEntityObjectId || step?.lookupEntityObjectId) return "lookup_entity";
  if (step?.MathOperator || step?.mathOperator) return "math";
  return key ? "predefined" : "";
};

const activeTransformInputs = (inputs) => asArray(inputs).filter((input) => input?.isDeleted !== true && input?.isActive !== false);

const getLookupStepValidationWarnings = (step, context = {}) => {
  const kind = normalizeTransformStepKind(step);
  if (kind !== "lookup_source" && kind !== "lookup_entity") return [];

  const objects = asArray(kind === "lookup_entity" ? context.lookupEntityObjects : context.lookupSourceObjects);
  const lookupLabel = kind === "lookup_entity" ? "Entity lookup" : "Source lookup";
  const lookupObjectId =
    step?.lookupObjectId ||
    step?.LookupObjectId ||
    step?.lookupEntityObjectId ||
    step?.LookupEntityObjectId ||
    step?.lookupSourceObjectId ||
    step?.LookupSourceObjectId ||
    null;
  const searchFieldId =
    step?.lookupSearchFieldId ||
    step?.LookupSearchFieldId ||
    step?.lookupEntitySearchFieldId ||
    step?.LookupEntitySearchFieldId ||
    step?.lookupSourceSearchFieldId ||
    step?.LookupSourceSearchFieldId ||
    null;
  const resultFieldId =
    step?.lookupResultFieldId ||
    step?.LookupResultFieldId ||
    step?.lookupEntityResultFieldId ||
    step?.LookupEntityResultFieldId ||
    step?.lookupSourceResultFieldId ||
    step?.LookupSourceResultFieldId ||
    null;

  if (objects.length === 0) return [`${lookupLabel} is unavailable because no lookup objects are available.`];
  if (!lookupObjectId) return [`${lookupLabel} requires a lookup object.`];

  const lookupObject = objects.find((item) => item?.id === lookupObjectId || item?._id === lookupObjectId);
  if (!lookupObject) return [`${lookupLabel} uses a lookup object that is no longer available.`];

  const fields = asArray(lookupObject?.fields || lookupObject?.Fields).filter((field) => field?.id || field?._id);
  const hasField = (fieldId) => fields.some((field) => (field?.id || field?._id) === fieldId);
  if (fields.length === 0) return [`${lookupLabel} lookup object has no selectable fields.`];
  if (!searchFieldId) return [`${lookupLabel} requires a search field.`];
  if (!hasField(searchFieldId)) return [`${lookupLabel} search field is no longer available.`];
  if (!resultFieldId) return [`${lookupLabel} requires a result field.`];
  if (!hasField(resultFieldId)) return [`${lookupLabel} result field is no longer available.`];
  return [];
};

const getMathStepValidationWarnings = (step, hasAdditionalInputs = false) => {
  if (normalizeTransformStepKind(step) !== "math") return [];
  const warnings = [];
  const operator = String(step?.mathOperator || step?.MathOperator || "").trim();
  if (!operator) {
    warnings.push("Math step requires an operation.");
    return warnings;
  }
  if (!SUPPORTED_MATH_OPERATORS.has(operator)) warnings.push(`Math operation '${operator}' is not supported.`);

  const operandValue = step?.operandValue ?? step?.OperandValue ?? "";
  const needsConstant = MATH_OPERATORS_REQUIRING_OPERAND.has(operator) && !hasAdditionalInputs;
  if (needsConstant && !String(operandValue).trim()) warnings.push(`${step?.displayName || operator} requires a value.`);
  if ((operator === "Divide" || operator === "Modulo") && String(operandValue).trim() && Number(operandValue) === 0) {
    warnings.push(`${step?.displayName || operator} cannot use 0.`);
  }

  const scale = step?.scale ?? step?.Scale;
  if (MATH_OPERATORS_REQUIRING_SCALE.has(operator) && scale != null && scale !== "" && !Number.isFinite(Number(scale))) {
    warnings.push(`${step?.displayName || operator} scale must be a number.`);
  }
  return warnings;
};

const getTransformStepValidationWarnings = (step, context = {}) => {
  if (!step || step?.isDeleted === true || step?.isActive === false) return [];
  const kind = normalizeTransformStepKind(step);
  if (kind === "custom") {
    const warnings = [];
    if (!String(step?.displayName || step?.customName || step?.CustomName || "").trim()) warnings.push("Custom Name is required for custom step(s).");
    if (!String(step?.code || step?.customTransformScript || step?.CustomTransformScript || step?.transformCode || step?.TransformCode || "").trim()) {
      warnings.push("Custom step has empty code.");
    }
    return warnings;
  }
  if (kind === "lookup_source" || kind === "lookup_entity") return getLookupStepValidationWarnings(step, context);
  if (kind === "math") return getMathStepValidationWarnings(step, context.hasAdditionalInputs === true);
  return [];
};

const getTransformInputValidationWarnings = (inputs, sourceById) =>
  activeTransformInputs(inputs).flatMap((input, index) => {
    const label = input?.inputName || input?.fieldName || `Input ${index + 1}`;
    if (!input?.sourceFieldId) return [`${label} needs a source field.`];
    if (!sourceById.has(input.sourceFieldId)) return [`${label} source field is no longer available.`];
    return [];
  });

const getMappingValidationWarnings = (edge, sourceById, entityById, context = {}) => {
  const warnings = [];
  const sourceField = sourceById.get(edge.sourceFieldId);
  const entityField = entityById.get(edge.entityFieldId);
  if (!entityField) warnings.push("Mapping target field is no longer available.");

  const steps = asArray(edge?.transformSteps);
  const activeSteps = steps.filter((step) => step?.isDeleted !== true && step?.isActive !== false);
  const transformInputs = activeTransformInputs(edge?.transformFields);
  const hasTransform = activeSteps.length > 0 || !!String(edge.transformKey || "").trim() || !!String(edge.transformCode || "").trim();
  if (String(edge.transformKey || "").toLowerCase() === "custom" && !String(edge.transformCode || "").trim()) {
    warnings.push("Custom transform code is required.");
  }
  warnings.push(...getTransformInputValidationWarnings(transformInputs, sourceById));
  activeSteps.forEach((step) => warnings.push(...getTransformStepValidationWarnings(step, { ...context, hasAdditionalInputs: transformInputs.length > 0 })));
  if (edge?.isConstant) return warnings;

  if (!sourceField) warnings.push("Mapping source field is no longer available.");
  if (!sourceField || !entityField) return warnings;
  const sameType = typeKey(sourceField) === typeKey(entityField);
  if (!sameType && !hasTransform) warnings.push("Type mismatch: add at least one transform step.");
  return warnings;
};

const edgeInvalid = (edge, sourceById, entityById, context = {}) => {
  return getMappingValidationWarnings(edge, sourceById, entityById, context).length > 0;
};

const getAggregateReducerInputFields = (aggregate, context = {}) => {
  const sourceById = context.sourceFieldById || new Map();
  const entityById = context.entityFieldById || new Map();
  const fields = [];
  const seen = new Set();
  const addField = (field, id) => {
    const key = String(id || field?.id || "");
    if (!field || !key || seen.has(key)) return;
    seen.add(key);
    fields.push(field);
  };

  addField(sourceById.get(aggregate?.inputSourceFieldId), aggregate?.inputSourceFieldId);
  addField(entityById.get(aggregate?.inputEntityFieldId), aggregate?.inputEntityFieldId);
  activeTransformInputs(aggregate?.transformFields).forEach((input) => {
    addField(sourceById.get(input?.sourceFieldId), input?.sourceFieldId);
  });
  return fields;
};

const getAggregateReducerTypeWarnings = (aggregate, context = {}) => {
  const reducer = aggregate?.reducer || "Sum";
  if (!["Sum", "Average", "Product"].includes(reducer)) return [];
  const hasPreTransform = asArray(aggregate?.preTransformSteps).some((step) => step?.isDeleted !== true && step?.isActive !== false);
  if (hasPreTransform) return [];

  const inputs = getAggregateReducerInputFields(aggregate, context);
  const invalidInputs = inputs.filter((field) => !isNumericType(field));
  if (invalidInputs.length === 0) return [];
  return [
    `${reducer} requires numeric input. Add a pre-transform or remove non-numeric input(s): ${invalidInputs
      .map((field) => getFieldValidationLabel(field))
      .join(", ")}.`,
  ];
};

const getAggregateValidationWarnings = (aggregate, context = {}) => {
  const warnings = [];
  const sourceById = context.sourceFieldById || new Map();
  const entityById = context.entityFieldById || new Map();
  const activeEdges = asArray(context.activeEdges);
  const reducer = aggregate?.reducer || "Sum";
  const reducerIsValid = LOOP_AGGREGATE_REDUCERS.some((option) => option?.Value === reducer);
  const hasInput =
    Boolean(aggregate?.inputSourceFieldId) ||
    Boolean(aggregate?.inputEntityFieldId) ||
    Boolean(String(aggregate?.inputPath || "").trim());

  if (!aggregate?.targetEntityFieldId) warnings.push("Aggregate needs a target field.");
  if (aggregate?.targetEntityFieldId && !entityById.has(aggregate.targetEntityFieldId)) warnings.push("Aggregate target field is no longer available.");
  if (!hasInput) warnings.push("Aggregate needs at least one input.");
  if (aggregate?.inputSourceFieldId && !sourceById.has(aggregate.inputSourceFieldId)) warnings.push("Aggregate source input field is no longer available.");
  if (aggregate?.inputEntityFieldId && !entityById.has(aggregate.inputEntityFieldId)) warnings.push("Aggregate entity input field is no longer available.");
  if (aggregate?.inputEntityFieldId && !activeEdges.some((edge) => edge?.entityFieldId === aggregate.inputEntityFieldId)) {
    warnings.push("Aggregate entity input must be mapped inside the active loop before it can be reduced.");
  }
  if (aggregate?.inputEntityFieldId) {
    const inputEdge = activeEdges.find((edge) => edge?.entityFieldId === aggregate.inputEntityFieldId);
    if (inputEdge && getMappingValidationWarnings(inputEdge, sourceById, entityById, context).length > 0) {
      warnings.push("Aggregate entity input mapping is invalid.");
    }
  }
  if (!reducerIsValid) warnings.push("Aggregate reducer is not supported.");
  warnings.push(...getAggregateReducerTypeWarnings(aggregate, context));

  const transformInputs = activeTransformInputs(aggregate?.transformFields);
  warnings.push(...getTransformInputValidationWarnings(transformInputs, sourceById));
  asArray(aggregate?.preTransformSteps).forEach((step) =>
    warnings.push(...getTransformStepValidationWarnings(step, { ...context, hasAdditionalInputs: transformInputs.length > 0 }))
  );
  asArray(aggregate?.transformSteps).forEach((step) =>
    warnings.push(...getTransformStepValidationWarnings(step, { ...context, hasAdditionalInputs: false }))
  );
  return warnings;
};

const getLoopValidation = (loop, context = {}) => {
  if (!loop || loop?.isDeleted === true || loop?.isActive === false) return { warnings: [], invalidFields: {} };
  const warnings = [];
  const invalidFields = {};
  const addWarning = (field, message) => {
    warnings.push(message);
    if (field) invalidFields[field] = true;
  };
  const sourceFields = asArray(context.sourceFields);
  const entityFieldById = context.entityFieldById || new Map();
  const sourceFieldExists = (fieldId) => sourceFields.some((field) => field?.id === fieldId);
  const collectionFieldExists = (fieldId) => sourceFields.some((field) => field?.id === fieldId && isSourceCollectionField(field));
  const sourceMode = getEffectiveLoopSourceMode(loop);
  const targetMode =
    sourceMode === "GroupedRows"
      ? "FlatRows"
      : loop?.targetMode || (loop?.targetContainerEntityObjectFieldId ? "NestedContainer" : "FlatRows");

  if (!loop?.sourceObjectId && !context.sourceObjectId) addWarning("loopSourceMode", "Loop needs a source object.");
  if (!SUPPORTED_LOOP_SOURCE_MODES.has(sourceMode)) addWarning("loopSourceMode", "Loop source mode is not supported.");
  if (!SUPPORTED_LOOP_TARGET_MODES.has(targetMode)) addWarning("targetContainerEntityObjectFieldId", "Loop target mode is not supported.");

  if (sourceMode === "InlineCollection" && !loop.collectionSourceFieldId && !String(loop.collectionPath || "").trim()) {
    addWarning("collectionSourceFieldId", "Inline collection loops need a collection field or collection path before Save.");
  }
  if (sourceMode === "InlineCollection" && loop.collectionSourceFieldId && !collectionFieldExists(loop.collectionSourceFieldId)) {
    addWarning("collectionSourceFieldId", "Selected collection field is no longer available.");
  }
  if (sourceMode === "GroupedRows" && !loop.groupBySourceFieldId) {
    addWarning("groupBySourceFieldId", "Grouped row loops need a group key before Save.");
  }
  if (sourceMode === "GroupedRows" && loop.groupBySourceFieldId && !sourceFieldExists(loop.groupBySourceFieldId)) {
    addWarning("groupBySourceFieldId", "Selected group key is no longer available.");
  }
  if (sourceMode === "GroupedRows" && loop.sortSourceFieldId && !sourceFieldExists(loop.sortSourceFieldId)) {
    addWarning("sortSourceFieldId", "Selected sort key is no longer available.");
  }
  if (targetMode === "NestedContainer" && !loop.targetContainerEntityObjectFieldId) {
    addWarning("targetContainerEntityObjectFieldId", "Nested loop output needs a target container.");
  }
  if (targetMode === "NestedContainer" && loop.targetContainerEntityObjectFieldId && !entityFieldById.has(loop.targetContainerEntityObjectFieldId)) {
    addWarning("targetContainerEntityObjectFieldId", "Selected target container is no longer available.");
  }

  return { warnings, invalidFields };
};

const compactFieldForAi = (field) => ({
  id: field?.id || null,
  fieldName: field?.fieldName || "",
  displayName: field?.displayName || "",
  dataTypeName: field?.dataTypeName || "",
  description: field?.description || "",
  aiPromptContext: field?.aiPromptContext || "",
  isRequired: field?.raw?.IsRequired === true || field?.isRequired === true,
});

const compactEdgeForAi = (edge, sourceFieldById, entityFieldById) => {
  const sourceField = edge?.isConstant ? null : sourceFieldById.get(edge?.sourceFieldId);
  const entityField = entityFieldById.get(edge?.entityFieldId);
  return {
    id: edge?.id || null,
    sourceObjectId: edge?.sourceObjectId || null,
    sourceFieldId: edge?.sourceFieldId || null,
    sourceFieldName: sourceField?.fieldName || "",
    sourceFieldDisplayName: sourceField?.displayName || "",
    entityFieldId: edge?.entityFieldId || null,
    entityFieldName: entityField?.fieldName || "",
    entityFieldDisplayName: entityField?.displayName || "",
    isConstant: edge?.isConstant === true,
    constantValue: edge?.constantValue || "",
    transformDisplayName: edge?.transformDisplayName || "",
    transformCode: edge?.transformCode || "",
    transformSteps: asArray(edge?.transformSteps).map((step) => ({
      kind: step?.kind || "",
      displayName: step?.displayName || "",
      key: step?.key || null,
      code: step?.code || "",
      isActive: step?.isActive !== false,
    })),
    importance: edge?.importance,
    isActive: edge?.isActive !== false,
  };
};

const EntityFieldMapDesigner = ({
  entityObject,
  sourceObjects,
  initialMapsBySourceId,
  dataTypes,
  fieldItemMetadata,
  entityObjectItemMetadata,
  sourceObjectFieldMapItemMetadata,
  sourceObjectFieldTransformScriptItemMetadata,
  lookupSourceObjects,
  lookupEntityObjects,
  transformCatalog,
  allowMultiSource = false,
  onSaveMap,
  onEntityFieldUpdate,
  onAiSuggestForField,
  onAiSuggestForEntityField,
  onAiBuildTransform,
  onAiContextChange,
  aiDraftUpdate,
  initialLoopMapsBySourceId,
  onSaveLoops,
}) => {
  const [activeSourceId, setActiveSourceId] = useState(asArray(sourceObjects)[0]?.id || "");
  const [mapsBySourceId, setMapsBySourceId] = useState({});
  const [savedBySourceId, setSavedBySourceId] = useState({});
  const [loopMapsBySourceId, setLoopMapsBySourceId] = useState({});
  const [savedLoopMapsBySourceId, setSavedLoopMapsBySourceId] = useState({});
  const [activeLoopId, setActiveLoopId] = useState(null);
  const [loopDialog, setLoopDialog] = useState(null);
  const [loopCanvasMode, setLoopCanvasMode] = useState("map");
  const [selectedAggregateId, setSelectedAggregateId] = useState(null);
  const [aggregateDialog, setAggregateDialog] = useState(null);
  const [aggregateDialogTab, setAggregateDialogTab] = useState("settings");
  const [aggregateEditorValidation, setAggregateEditorValidation] = useState({
    preTransform: { canApply: true, warnings: [] },
    transform: { canApply: true, warnings: [] },
  });
  const [deleteLoopConfirm, setDeleteLoopConfirm] = useState(null);
  const [searchSource, setSearchSource] = useState("");
  const [searchEntity, setSearchEntity] = useState("");
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [transformEdgeId, setTransformEdgeId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [editEntityObjectOpen, setEditEntityObjectOpen] = useState(false);
  const [entityFields, setEntityFields] = useState(asArray(entityObject?.fields));
  const [savedEntityFields, setSavedEntityFields] = useState(cloneFields(entityObject?.fields));
  const [entityObjectDraftRaw, setEntityObjectDraftRaw] = useState(entityObject?.raw || {});
  const [savedEntityObjectRaw, setSavedEntityObjectRaw] = useState(entityObject?.raw || {});
  const [, setPendingFieldPatches] = useState({});
  const [savedEntityFieldOrder, setSavedEntityFieldOrder] = useState(asArray(entityObject?.fields).map((field) => field.id));
  const [aiConfirm, setAiConfirm] = useState(null);
  const [, setDraftRestored] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [reorderEntityFieldId, setReorderEntityFieldId] = useState(null);
  const [layout, setLayout] = useState({});
  const [inputLayout, setInputLayout] = useState({});
  const [aggregateLayout, setAggregateLayout] = useState({});
  const [aggregateInputLayout, setAggregateInputLayout] = useState({});
  const [loopLayout, setLoopLayout] = useState({});
  const [aiFieldLoadingTarget, setAiFieldLoadingTarget] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [activeUtilityPanel, setActiveUtilityPanel] = useState("loops");
  const [utilityMenuOpen, setUtilityMenuOpen] = useState(false);
  const [selectedSearchSourceId, setSelectedSearchSourceId] = useState("");
  const [selectedSearchEdgeId, setSelectedSearchEdgeId] = useState("");
  const [pendingOpenMapping, setPendingOpenMapping] = useState(null);
  const [pendingSelectMapping, setPendingSelectMapping] = useState(null);
  const isSingleSourceOnly = useMemo(() => {
    const raw = entityObject?.raw || {};
    return (
      raw?.SourceObjectOfRecordOnly === true ||
      entityObject?.sourceObjectOfRecordOnly === true
    );
  }, [entityObject]);
  const showLoopsPanel = activeUtilityPanel === "loops";
  const showArchivedMappings = activeUtilityPanel === "search";

  const sourceRowRefs = useRef({});
  const entityRowRefs = useRef({});
  const canvasRef = useRef(null);
  const gutterRef = useRef(null);
  const aggregatePreTransformEditorRef = useRef(null);
  const aggregateTransformEditorRef = useRef(null);
  const lastAiContextRef = useRef("");
  const lastAiDraftUpdateRef = useRef(null);

  useEffect(() => {
    setEntityFields(asArray(entityObject?.fields));
    setSavedEntityFields(cloneFields(entityObject?.fields));
    setEntityObjectDraftRaw(entityObject?.raw || {});
    setSavedEntityObjectRaw(entityObject?.raw || {});
    setPendingFieldPatches({});
    setSavedEntityFieldOrder(asArray(entityObject?.fields).map((field) => field.id));
  }, [entityObject]);

  useEffect(() => {
    const nextMaps = {};
    const nextSaved = {};
    const nextLoops = {};
    const nextSavedLoops = {};
    asArray(sourceObjects).forEach((source) => {
      const initialMap = initialMapsBySourceId?.[source.id] || buildEmptyFieldMap(entityObject?.id, source.id);
      const initialLoops = cloneLoops(initialLoopMapsBySourceId?.[source.id] || []);
      const storageKey = draftKey(entityObject?.id, source.id, initialMap.id || "new");
      const loopsStorageKey = loopDraftKey(entityObject?.id, source.id);
      const draftRaw = localStorage.getItem(storageKey);
      const loopDraftRaw = localStorage.getItem(loopsStorageKey);
      let mapToUse = cloneMap(initialMap);
      let loopsToUse = normalizeLoopSourceModes(cloneLoops(initialLoops));
      if (draftRaw) {
        try {
          mapToUse = rehydrateDraftMap(JSON.parse(draftRaw), initialMap);
          setDraftRestored(true);
        } catch {
          mapToUse = cloneMap(initialMap);
        }
      }
      if (loopDraftRaw) {
        try {
          loopsToUse = normalizeLoopSourceModes(cloneLoops(JSON.parse(loopDraftRaw)));
          setDraftRestored(true);
        } catch {
          loopsToUse = normalizeLoopSourceModes(cloneLoops(initialLoops));
        }
      }
      nextMaps[source.id] = mapToUse;
      nextSaved[source.id] = cloneMap(initialMap);
      nextLoops[source.id] = loopsToUse;
      nextSavedLoops[source.id] = cloneLoops(initialLoops);
    });
    setMapsBySourceId(nextMaps);
    setSavedBySourceId(nextSaved);
    setLoopMapsBySourceId(nextLoops);
    setSavedLoopMapsBySourceId(nextSavedLoops);
    setActiveLoopId(null);
  }, [sourceObjects, initialMapsBySourceId, initialLoopMapsBySourceId, entityObject?.id]);

  const sourceFieldById = useMemo(() => {
    const activeSource = asArray(sourceObjects).find((x) => x.id === activeSourceId);
    return new Map(flattenFieldsWithCollectionChildren(activeSource?.fields, "source").map((x) => [x.id, x]));
  }, [sourceObjects, activeSourceId]);

  const entityFieldById = useMemo(() => new Map(flattenFieldsWithCollectionChildren(entityFields, "entity").map((x) => [x.id, x])), [entityFields]);

  const activeSource = useMemo(() => asArray(sourceObjects).find((x) => x.id === activeSourceId), [sourceObjects, activeSourceId]);
  const activeMap = mapsBySourceId[activeSourceId] || buildEmptyFieldMap(entityObject?.id, activeSourceId);
  const activeLoops = useMemo(() => normalizeLoopSourceModes(cloneLoops(loopMapsBySourceId[activeSourceId] || [])), [loopMapsBySourceId, activeSourceId]);
  const activeVisibleLoops = useMemo(
    () => activeLoops.filter((loop) => loop?.isDeleted !== true && loop?.isActive !== false),
    [activeLoops]
  );
  const activeLoop = useMemo(() => activeLoops.find((loop) => loop?.id === activeLoopId) || null, [activeLoops, activeLoopId]);
  const loopDisplayNumberById = useMemo(() => {
    const countsBySourceMode = new Map();
    const numberById = new Map();
    activeVisibleLoops.forEach((loop) => {
      const sourceMode = getEffectiveLoopSourceMode(loop);
      const nextCount = (countsBySourceMode.get(sourceMode) || 0) + 1;
      countsBySourceMode.set(sourceMode, nextCount);
      if (loop?.id) numberById.set(loop.id, nextCount);
    });
    return numberById;
  }, [activeVisibleLoops]);
  const activeLoopNumber = useMemo(
    () => Math.max(1, loopDisplayNumberById.get(activeLoopId) || 1),
    [activeLoopId, loopDisplayNumberById]
  );
  const getNextLoopNumber = useCallback(() => {
    const maxAliasNumber = activeLoops.reduce((max, loop) => Math.max(max, getLoopNumberFromAlias(loop)), 0);
    return Math.max(maxAliasNumber, activeVisibleLoops.length) + 1;
  }, [activeLoops, activeVisibleLoops.length]);
  const getNextLoopDisplayNumber = useCallback(
    (sourceMode = "GroupedRows") =>
      activeVisibleLoops.filter((loop) => getEffectiveLoopSourceMode(loop) === sourceMode).length + 1,
    [activeVisibleLoops]
  );
  const activeScopeLabel = activeLoop ? getLoopDisplayName(activeLoop, activeLoopNumber) : "Root";
  const activeScopeLoopId = activeLoop ? activeLoop.id : null;
  const activeLoopSourceCollectionField = useMemo(() => {
    if (!activeLoop || getEffectiveLoopSourceMode(activeLoop) !== "InlineCollection") return null;
    if (!activeLoop?.collectionSourceFieldId) return null;
    return sourceFieldById.get(activeLoop.collectionSourceFieldId) || null;
  }, [activeLoop, sourceFieldById]);
  const activeLoopTargetCollectionField = useMemo(() => {
    if (!activeLoop || getEffectiveLoopSourceMode(activeLoop) === "GroupedRows") return null;
    if (!activeLoop?.targetContainerEntityObjectFieldId) return null;
    return entityFieldById.get(activeLoop.targetContainerEntityObjectFieldId) || null;
  }, [activeLoop, entityFieldById]);
  const activeEntityCollectionChildFields = useMemo(
    () => getCollectionScopedFields(entityFields, activeLoopTargetCollectionField, "entity"),
    [activeLoopTargetCollectionField, entityFields]
  );
  const activeSourceFieldScope = useMemo(
    () => {
      const scopedFields = getCollectionScopedFields(activeSource?.fields, activeLoopSourceCollectionField, "source");
      if (activeLoop && getEffectiveLoopSourceMode(activeLoop) === "GroupedRows") {
        return scopedFields.filter((field) => !isSourceCollectionField(field));
      }
      if (activeLoopSourceCollectionField && activeLoopTargetCollectionField) {
        return filterFieldsByTargetCollectionShape(scopedFields, activeEntityCollectionChildFields, activeLoopSourceCollectionField);
      }
      return scopedFields;
    },
    [activeEntityCollectionChildFields, activeLoop, activeLoopSourceCollectionField, activeLoopTargetCollectionField, activeSource?.fields]
  );
  const activeEntityFieldScope = useMemo(
    () => {
      const scopedFields =
        activeLoop && getEffectiveLoopSourceMode(activeLoop) === "GroupedRows"
          ? activeEntityCollectionChildFields.filter((field) => !isTargetContainerField(field))
          : activeEntityCollectionChildFields;
      if (activeLoop && loopCanvasMode === "aggregate") {
        const scopedIds = new Set(scopedFields.map((field) => field.id));
        return [...scopedFields, ...asArray(entityFields).filter((field) => !scopedIds.has(field.id) && !isTargetContainerField(field))];
      }
      return scopedFields;
    },
    [activeEntityCollectionChildFields, activeLoop, entityFields, loopCanvasMode]
  );
  const sourceHeaderLabel = activeLoopSourceCollectionField ? "Source Collection" : "Source";
  const entityHeaderLabel = activeLoopTargetCollectionField ? "Entity Collection" : "Entity";
  const sourceHeaderName = activeLoopSourceCollectionField
    ? getFieldSummaryLabel(activeLoopSourceCollectionField, activeLoop?.collectionPath || activeSource?.displayName)
    : activeSource?.displayName;
  const entityHeaderName = activeLoopTargetCollectionField
    ? getFieldSummaryLabel(activeLoopTargetCollectionField, entityObjectDraftRaw?.DisplayName || entityObject?.displayName)
    : entityObjectDraftRaw?.DisplayName || entityObject?.displayName;
  const allActiveEdges = useMemo(() => asArray(activeMap?.items).filter(isActiveEdge), [activeMap]);
  const activeEdges = useMemo(
    () => allActiveEdges.filter((edge) => (edge?.loopMapId || null) === activeScopeLoopId),
    [allActiveEdges, activeScopeLoopId]
  );
  const isLoopAggregateMode = !!activeLoop && loopCanvasMode === "aggregate";
  const activeLoopAggregates = useMemo(
    () => asArray(activeLoop?.aggregates).filter((aggregate) => aggregate?.isDeleted !== true && aggregate?.isActive !== false),
    [activeLoop]
  );
  const validationLookupContext = useMemo(
    () => ({
      lookupSourceObjects,
      lookupEntityObjects,
    }),
    [lookupEntityObjects, lookupSourceObjects]
  );
  const activeAggregateEdges = useMemo(
    () =>
      activeLoopAggregates
        .map((aggregate) => {
          const inputSourceField = sourceFieldById.get(aggregate?.inputSourceFieldId);
          const inputEntityField = entityFieldById.get(aggregate?.inputEntityFieldId);
          const targetEntityField = entityFieldById.get(aggregate?.targetEntityFieldId);
          if (!targetEntityField) return null;
          if (
            !inputSourceField &&
            !inputEntityField &&
            !String(aggregate?.inputPath || "").trim() &&
            !aggregate?.inputSourceFieldId &&
            !aggregate?.inputEntityFieldId
          ) {
            return null;
          }
          const additionalInputLabels = asArray(aggregate?.transformFields)
            .filter((input) => input?.isDeleted !== true && input?.isActive !== false)
            .map((input) => sourceFieldById.get(input?.sourceFieldId))
            .filter(Boolean)
            .map((field) => field?.displayName || field?.fieldName || "Input");
          const inputLabel =
            inputSourceField?.displayName ||
            inputSourceField?.fieldName ||
            inputEntityField?.displayName ||
            inputEntityField?.fieldName ||
            aggregate?.inputPath ||
            "Input";
          const fullInputLabel = [inputLabel, ...additionalInputLabels].join(" + ");
          const targetLabel = targetEntityField?.displayName || targetEntityField?.fieldName || "Target";
          const reducer = aggregate?.reducer || "Sum";
          const validationWarnings = getAggregateValidationWarnings(aggregate, {
            ...validationLookupContext,
            sourceFieldById,
            entityFieldById,
            activeEdges,
          });
          return {
            ...aggregate,
            loopMapId: activeLoop?.id || null,
            inputLabel,
            targetLabel,
            invalid: validationWarnings.length > 0,
            validationWarnings,
            title: `${reducer}(${fullInputLabel}) -> ${targetLabel}${validationWarnings.length > 0 ? ` - ${validationWarnings.join(" ")}` : ""}`,
          };
        })
        .filter(Boolean),
    [activeEdges, activeLoop?.id, activeLoopAggregates, entityFieldById, sourceFieldById, validationLookupContext]
  );
  const loopValidationById = useMemo(() => {
    const results = new Map();
    activeVisibleLoops.forEach((loop) => {
      const loopEdges = allActiveEdges.filter((edge) => edge?.loopMapId === loop?.id);
      const loopValidation = getLoopValidation(loop, {
        sourceFields: activeSource?.fields,
        sourceObjectId: activeSourceId,
        entityFieldById,
      });
      const aggregateWarnings = asArray(loop?.aggregates)
        .filter((aggregate) => aggregate?.isDeleted !== true && aggregate?.isActive !== false)
        .flatMap((aggregate) =>
          getAggregateValidationWarnings(aggregate, {
            ...validationLookupContext,
            sourceFieldById,
            entityFieldById,
            activeEdges: loopEdges,
          }).map((warning) => `Aggregate: ${warning}`)
        );
      results.set(loop.id, {
        invalidFields: loopValidation.invalidFields,
        warnings: [...loopValidation.warnings, ...aggregateWarnings],
      });
    });
    return results;
  }, [activeSource?.fields, activeSourceId, activeVisibleLoops, allActiveEdges, entityFieldById, sourceFieldById, validationLookupContext]);
  const activeLoopEdges = useMemo(
    () =>
      activeVisibleLoops
        .map((loop, index) => {
          if (getEffectiveLoopSourceMode(loop) !== "InlineCollection" || !loop?.collectionSourceFieldId) return null;
          if (!sourceFieldById.has(loop.collectionSourceFieldId)) return null;
          const targetEntityFieldIds = new Set();
          if (loop?.targetContainerEntityObjectFieldId && entityFieldById.has(loop.targetContainerEntityObjectFieldId)) {
            targetEntityFieldIds.add(loop.targetContainerEntityObjectFieldId);
          }
          allActiveEdges
            .filter((edge) => edge?.loopMapId === loop?.id && edge?.entityFieldId && entityFieldById.has(edge.entityFieldId))
            .forEach((edge) => targetEntityFieldIds.add(edge.entityFieldId));
          asArray(loop?.aggregates)
            .filter((aggregate) => aggregate?.isDeleted !== true && aggregate?.isActive !== false && aggregate?.targetEntityFieldId && entityFieldById.has(aggregate.targetEntityFieldId))
            .forEach((aggregate) => targetEntityFieldIds.add(aggregate.targetEntityFieldId));
          const displayName = getLoopDisplayName(loop, loopDisplayNumberById.get(loop.id) || index + 1);
          const validation = loopValidationById.get(loop.id);
          return {
            id: loop.id,
            sourceFieldId: loop.collectionSourceFieldId,
            targetEntityFieldIds: Array.from(targetEntityFieldIds),
            displayName,
            invalid: validation?.warnings?.length > 0,
            title: `${displayName} collection loop`,
          };
        })
        .filter(Boolean),
    [activeVisibleLoops, allActiveEdges, entityFieldById, loopDisplayNumberById, loopValidationById, sourceFieldById]
  );
  const mapHasChanges = useCallback(
    (sourceId) => {
      const current = mapsBySourceId[sourceId] || buildEmptyFieldMap(entityObject?.id, sourceId);
      const saved = savedBySourceId[sourceId];
      return !saved ? asArray(current?.items).length > 0 : JSON.stringify(current) !== JSON.stringify(saved);
    },
    [mapsBySourceId, savedBySourceId, entityObject?.id]
  );
  const changedMapSourceIds = useMemo(
    () =>
      asArray(sourceObjects)
        .map((source) => source?.id)
        .filter((id) => !!id && mapHasChanges(id)),
    [sourceObjects, mapHasChanges]
  );
  const loopsHaveChanges = useCallback(
    (sourceId) =>
      JSON.stringify(normalizeLoopSourceModes(cloneLoops(loopMapsBySourceId[sourceId] || []))) !==
      JSON.stringify(cloneLoops(savedLoopMapsBySourceId[sourceId] || [])),
    [loopMapsBySourceId, savedLoopMapsBySourceId]
  );
  const changedLoopSourceIds = useMemo(
    () =>
      asArray(sourceObjects)
        .map((source) => source?.id)
        .filter((id) => !!id && loopsHaveChanges(id)),
    [sourceObjects, loopsHaveChanges]
  );
  const hasEntityOrderChanges = useMemo(() => {
    const currentOrder = asArray(entityFields).map((field) => field.id);
    return !sameIdOrder(currentOrder, savedEntityFieldOrder);
  }, [entityFields, savedEntityFieldOrder]);
  const hasEntityFieldChanges = useMemo(() => {
    return JSON.stringify(asArray(entityFields)) !== JSON.stringify(asArray(savedEntityFields));
  }, [entityFields, savedEntityFields]);
  const hasEntityObjectChanges = useMemo(() => {
    return JSON.stringify(entityObjectDraftRaw || {}) !== JSON.stringify(savedEntityObjectRaw || {});
  }, [entityObjectDraftRaw, savedEntityObjectRaw]);
  const hasUnsavedChanges = useMemo(() => {
    const hasMapChanges = changedMapSourceIds.length > 0;
    return hasMapChanges || changedLoopSourceIds.length > 0 || hasEntityOrderChanges || hasEntityFieldChanges || hasEntityObjectChanges;
  }, [changedMapSourceIds, changedLoopSourceIds, hasEntityOrderChanges, hasEntityFieldChanges, hasEntityObjectChanges]);
  const hasDraftAvailable = useMemo(() => {
    const hasMapChanges = changedMapSourceIds.length > 0;
    return hasMapChanges || changedLoopSourceIds.length > 0 || hasEntityOrderChanges || hasEntityFieldChanges || hasEntityObjectChanges;
  }, [changedMapSourceIds, changedLoopSourceIds, hasEntityOrderChanges, hasEntityFieldChanges, hasEntityObjectChanges]);
  const hasMappingsToClear = useMemo(() => asArray(activeMap?.items).length > 0, [activeMap]);

  useEffect(() => {
    if (activeLoop) return;
    setLoopCanvasMode("map");
    setSelectedAggregateId(null);
    setAggregateDialog(null);
    setAggregateDialogTab("settings");
  }, [activeLoop]);

  const sourceFields = useMemo(() => asArray(activeSourceFieldScope).filter((x) => searchFieldMatch(x, searchSource)), [activeSourceFieldScope, searchSource]);
  const filteredEntityFields = useMemo(() => asArray(activeEntityFieldScope).filter((x) => searchFieldMatch(x, searchEntity)), [activeEntityFieldScope, searchEntity]);
  const visibleSourceFields = useMemo(
    () => prioritizeFieldById(sourceFields, activeLoopSourceCollectionField?.id),
    [activeLoopSourceCollectionField?.id, sourceFields]
  );
  const visibleEntityFields = useMemo(
    () => prioritizeFieldById(filteredEntityFields, activeLoopTargetCollectionField?.id),
    [activeLoopTargetCollectionField?.id, filteredEntityFields]
  );
  const isEntitySearchActive = useMemo(() => String(searchEntity || "").trim().length > 0, [searchEntity]);

  const invalidEdges = useMemo(
    () => activeEdges.filter((edge) => edgeInvalid(edge, sourceFieldById, entityFieldById, validationLookupContext)),
    [activeEdges, sourceFieldById, entityFieldById, validationLookupContext]
  );
  const invalidAggregateEdges = useMemo(
    () => activeAggregateEdges.filter((edge) => edge?.invalid === true),
    [activeAggregateEdges]
  );
  const invalidLoopCount = useMemo(
    () => Array.from(loopValidationById.values()).filter((result) => result?.warnings?.length > 0).length,
    [loopValidationById]
  );
  const validationSummaryText = useMemo(
    () =>
      [
        invalidLoopCount > 0 ? `${invalidLoopCount} invalid loop(s)` : "",
        invalidAggregateEdges.length > 0 ? `${invalidAggregateEdges.length} invalid aggregate(s)` : "",
        invalidEdges.length > 0 ? `${invalidEdges.length} invalid mapping(s)` : "",
      ].filter(Boolean).join(", "),
    [invalidAggregateEdges.length, invalidEdges.length, invalidLoopCount]
  );
  const selectedMappingEdge = useMemo(
    () => activeEdges.find((edge) => edge.id === selectedEdgeId) || null,
    [activeEdges, selectedEdgeId]
  );

  const aiDesignerContext = useMemo(() => {
    const invalidDetails = invalidEdges.map((edge) => compactEdgeForAi(edge, sourceFieldById, entityFieldById));
    return {
      payloadKind: "EntityFieldMappingDesignerContextV1",
      entityObjectId: entityObject?.id || null,
      entityObject: {
        id: entityObject?.id || null,
        displayName: entityObjectDraftRaw?.DisplayName || entityObject?.displayName || "",
        objectName: entityObject?.objectName || "",
        fields: asArray(entityFields).map(compactFieldForAi),
      },
      activeSourceId,
      sourceObjectId: activeSourceId,
      activeSource: {
        id: activeSource?.id || null,
        displayName: activeSource?.displayName || "",
        objectName: activeSource?.objectName || "",
        fields: asArray(activeSource?.fields).map(compactFieldForAi),
      },
      allowMultiSource,
      hasUnsavedChanges,
      changedMapSourceIds,
      currentDraft: toDraftMap(activeMap),
      activeMappings: activeEdges.map((edge) => compactEdgeForAi(edge, sourceFieldById, entityFieldById)),
      invalidMappingCount: invalidEdges.length,
      invalidMappings: invalidDetails,
      selectedEdge: selectedMappingEdge ? compactEdgeForAi(selectedMappingEdge, sourceFieldById, entityFieldById) : null,
      selectedSourceField: selectedMappingEdge?.sourceFieldId ? compactFieldForAi(sourceFieldById.get(selectedMappingEdge.sourceFieldId)) : null,
      selectedEntityField: selectedMappingEdge?.entityFieldId ? compactFieldForAi(entityFieldById.get(selectedMappingEdge.entityFieldId)) : null,
      searchSource,
      searchEntity,
    };
  }, [
    activeEdges,
    activeMap,
    activeSource,
    activeSourceId,
    allowMultiSource,
    changedMapSourceIds,
    entityFieldById,
    entityFields,
    entityObject,
    entityObjectDraftRaw,
    hasUnsavedChanges,
    invalidEdges,
    searchEntity,
    searchSource,
    selectedMappingEdge,
    sourceFieldById,
  ]);

  useEffect(() => {
    if (typeof onAiContextChange !== "function") return;
    let contextText = "";
    try {
      contextText = JSON.stringify(aiDesignerContext);
    } catch {
      contextText = "";
    }
    if (contextText && contextText === lastAiContextRef.current) return;
    lastAiContextRef.current = contextText;
    onAiContextChange(aiDesignerContext);
  }, [aiDesignerContext, onAiContextChange]);

  useEffect(() => {
    const nextSourceId = asArray(sourceObjects).some((source) => source?.id === selectedSearchSourceId)
      ? selectedSearchSourceId
      : activeSourceId || asArray(sourceObjects)[0]?.id || "";
    if (nextSourceId !== selectedSearchSourceId) {
      setSelectedSearchSourceId(nextSourceId);
    }
  }, [sourceObjects, activeSourceId, selectedSearchSourceId]);

  const searchMappingSource = useMemo(
    () => asArray(sourceObjects).find((source) => source?.id === selectedSearchSourceId) || null,
    [sourceObjects, selectedSearchSourceId]
  );

  const searchMappingSourceFieldById = useMemo(
    () => new Map(asArray(searchMappingSource?.fields).map((field) => [field.id, field])),
    [searchMappingSource]
  );

  const searchMappingItems = useMemo(() => {
    const map = mapsBySourceId[selectedSearchSourceId] || buildEmptyFieldMap(entityObject?.id, selectedSearchSourceId);
    return asArray(map?.items).filter(
      (edge) =>
        !isDeletedEdge(edge) &&
        hasRenderableSource(edge, searchMappingSourceFieldById) &&
        hasRenderableEntity(edge, entityFieldById)
    );
  }, [mapsBySourceId, selectedSearchSourceId, entityObject?.id, searchMappingSourceFieldById, entityFieldById]);

  const searchMappingOptions = useMemo(
    () =>
      searchMappingItems.map((edge) => ({
        Value: edge.id,
        Text: buildMappingSearchLabel(edge, searchMappingSourceFieldById, entityFieldById),
        Important: isActiveEdge(edge),
        Sequence: -1,
      })),
    [searchMappingItems, searchMappingSourceFieldById, entityFieldById]
  );

  const selectedSearchMapping = useMemo(
    () => searchMappingItems.find((edge) => edge.id === selectedSearchEdgeId) || null,
    [searchMappingItems, selectedSearchEdgeId]
  );

  useEffect(() => {
    if (searchMappingOptions.length === 0) {
      if (selectedSearchEdgeId) setSelectedSearchEdgeId("");
      return;
    }
    if (!searchMappingOptions.some((item) => item.Value === selectedSearchEdgeId)) {
      setSelectedSearchEdgeId(searchMappingOptions[0]?.Value || "");
    }
  }, [searchMappingOptions, selectedSearchEdgeId]);

  useEffect(() => {
    if (!pendingOpenMapping) return;
    if (activeSourceId !== pendingOpenMapping.sourceId) return;
    const edge = asArray((mapsBySourceId[pendingOpenMapping.sourceId] || {}).items).find(
      (item) => item?.id === pendingOpenMapping.edgeId && isActiveEdge(item)
    );
    if (!edge) return;
    setSelectedEdgeId(edge.id);
    setTransformEdgeId(edge.id);
    setPendingOpenMapping(null);
  }, [pendingOpenMapping, activeSourceId, mapsBySourceId]);

  useEffect(() => {
    if (!pendingSelectMapping) return;
    if (activeSourceId !== pendingSelectMapping.sourceId) return;
    const edge = asArray((mapsBySourceId[pendingSelectMapping.sourceId] || {}).items).find(
      (item) => item?.id === pendingSelectMapping.edgeId && isActiveEdge(item)
    );
    if (!edge) {
      setPendingSelectMapping(null);
      return;
    }
    setSelectedEdgeId(edge.id);
    setPendingSelectMapping(null);
  }, [pendingSelectMapping, activeSourceId, mapsBySourceId]);

  const searchMappingSourceOptions = useMemo(
    () =>
      asArray(sourceObjects)
        .slice()
        .sort((left, right) => String(left?.displayName || "").localeCompare(String(right?.displayName || "")))
        .map((source) => ({
          Value: source.id,
          Text: source.displayName || source.name || source.id,
          Sequence: -1,
        })),
    [sourceObjects]
  );

  const setMapForSource = useCallback(
    (sourceId, updater) => {
      setMapsBySourceId((previous) => {
        const current = previous[sourceId] || buildEmptyFieldMap(entityObject?.id, sourceId);
        const next = typeof updater === "function" ? updater(current) : updater;
        const key = draftKey(entityObject?.id, sourceId, next.id || "new");
        try {
          localStorage.setItem(key, JSON.stringify(toDraftMap(next)));
        } catch (error) {
          try {
            localStorage.removeItem(key);
          } catch {
            // Ignore storage cleanup failures.
          }
          console.warn("Failed to persist mapping draft to localStorage.", error);
        }
        return { ...previous, [sourceId]: next };
      });
    },
    [entityObject?.id]
  );

  const setLoopsForSource = useCallback(
    (sourceId, updater) => {
      setLoopMapsBySourceId((previous) => {
        const current = cloneLoops(previous[sourceId] || []);
        const next = normalizeLoopSourceModes(cloneLoops(typeof updater === "function" ? updater(current) : updater));
        try {
          localStorage.setItem(loopDraftKey(entityObject?.id, sourceId), JSON.stringify(next));
        } catch (error) {
          try {
            localStorage.removeItem(loopDraftKey(entityObject?.id, sourceId));
          } catch {
            // Ignore storage cleanup failures.
          }
          console.warn("Failed to persist loop mapping draft to localStorage.", error);
        }
        return { ...previous, [sourceId]: next };
      });
    },
    [entityObject?.id]
  );

  const setActiveMap = useCallback(
    (updater) => {
      setMapForSource(activeSourceId, updater);
    },
    [activeSourceId, setMapForSource]
  );

  const setActiveLoops = useCallback(
    (updater) => {
      setLoopsForSource(activeSourceId, updater);
    },
    [activeSourceId, setLoopsForSource]
  );

  useEffect(() => {
    if (!activeLoopId) return;
    if (!activeVisibleLoops.some((loop) => loop?.id === activeLoopId)) {
      setActiveLoopId(null);
    }
  }, [activeLoopId, activeVisibleLoops]);

  const saveNow = useCallback(async () => {
    const sourceById = new Map(asArray(sourceObjects).map((x) => [x?.id, x]));
    const changedSourceIds = changedMapSourceIds;
    const hasFieldChanges = JSON.stringify(asArray(entityFields)) !== JSON.stringify(asArray(savedEntityFields));
    const hasObjectChanges = JSON.stringify(entityObjectDraftRaw || {}) !== JSON.stringify(savedEntityObjectRaw || {});

    if (changedLoopSourceIds.length > 0 && typeof onSaveLoops === "function") {
      const nextLoopsBySource = { ...loopMapsBySourceId };
      const nextSavedLoopsBySource = { ...savedLoopMapsBySourceId };

      for (const sourceId of changedLoopSourceIds) {
        const currentLoops = normalizeLoopSourceModes(cloneLoops(loopMapsBySourceId[sourceId] || []));
        const savedLoops = cloneLoops(savedLoopMapsBySourceId[sourceId] || []);
        const source = sourceById.get(sourceId);
        const sourceFieldLookup = new Map(flattenFieldsWithCollectionChildren(source?.fields, "source").map((field) => [field.id, field]));
        const activeMapForSource = mapsBySourceId[sourceId] || buildEmptyFieldMap(entityObject?.id, sourceId);
        const activeEdgesForSource = asArray(activeMapForSource?.items).filter(isActiveEdge);
        const invalidLoops = currentLoops
          .filter((loop) => loop?.isDeleted !== true && loop?.isActive !== false)
          .filter((loop) => {
            const loopEdges = activeEdgesForSource.filter((edge) => edge?.loopMapId === loop?.id);
            const loopWarnings = getLoopValidation(loop, {
              sourceFields: source?.fields,
              sourceObjectId: sourceId,
              entityFieldById,
            }).warnings;
            const aggregateWarnings = asArray(loop?.aggregates)
              .filter((aggregate) => aggregate?.isDeleted !== true && aggregate?.isActive !== false)
              .flatMap((aggregate) =>
                getAggregateValidationWarnings(aggregate, {
                  ...validationLookupContext,
                  sourceFieldById: sourceFieldLookup,
                  entityFieldById,
                  activeEdges: loopEdges,
                })
              );
            return loopWarnings.length > 0 || aggregateWarnings.length > 0;
          });
        if (invalidLoops.length > 0) return;
        const result = await onSaveLoops(sourceId, currentLoops, { previousLoops: savedLoops });
        if (!result?.Success) return;
        const savedLoopsForSource = cloneLoops(result.loops || currentLoops);
        nextLoopsBySource[sourceId] = savedLoopsForSource;
        nextSavedLoopsBySource[sourceId] = cloneLoops(savedLoopsForSource);
        localStorage.removeItem(loopDraftKey(entityObject?.id, sourceId));
      }

      setLoopMapsBySourceId(nextLoopsBySource);
      setSavedLoopMapsBySourceId(nextSavedLoopsBySource);
    }

    if (changedSourceIds.length > 0) {
      const nextMaps = { ...mapsBySourceId };
      const nextSaved = { ...savedBySourceId };

      for (const sourceId of changedSourceIds) {
        const current = mapsBySourceId[sourceId];
        const savedMap = savedBySourceId[sourceId];
        if (!current) continue;

        const sourceFieldLookup = new Map(flattenFieldsWithCollectionChildren(sourceById.get(sourceId)?.fields, "source").map((field) => [field.id, field]));
        const invalidForMap = asArray(current?.items).filter((edge) =>
          isActiveEdge(edge) && edgeInvalid(edge, sourceFieldLookup, entityFieldById, validationLookupContext)
        );
        if (invalidForMap.length > 0) return;

        const result = await onSaveMap(current, { previousMap: savedMap });
        if (!result?.Success) return;

        const nextSavedMap = result.map || current;
        nextMaps[sourceId] = cloneMap(nextSavedMap);
        nextSaved[sourceId] = cloneMap(nextSavedMap);
        const key = draftKey(entityObject?.id, sourceId, nextSavedMap.id || "new");
        localStorage.removeItem(key);
      }

      setMapsBySourceId(nextMaps);
      setSavedBySourceId(nextSaved);
    }

    if (hasFieldChanges || hasObjectChanges) {
      const result = await onEntityFieldUpdate(asArray(entityFields), entityObjectDraftRaw);
      if (!result?.Success) return;
      setSavedEntityFields(cloneFields(entityFields));
      setSavedEntityObjectRaw(entityObjectDraftRaw || {});
      setPendingFieldPatches({});
    }

    setSavedEntityFieldOrder(asArray(entityFields).map((field) => field.id));
    setDraftRestored(false);
  }, [
    entityObject?.id,
    mapsBySourceId,
    savedBySourceId,
    loopMapsBySourceId,
    savedLoopMapsBySourceId,
    sourceObjects,
    changedMapSourceIds,
    changedLoopSourceIds,
    entityFieldById,
    validationLookupContext,
    onSaveMap,
    onSaveLoops,
    onEntityFieldUpdate,
    entityFields,
    savedEntityFields,
    entityObjectDraftRaw,
    savedEntityObjectRaw,
  ]);

  const scheduleAutoSave = useCallback(() => {}, []);

  useEffect(() => {
    if (!aiDraftUpdate?.version || lastAiDraftUpdateRef.current === aiDraftUpdate.version) return;
    const targetSourceId = aiDraftUpdate.sourceObjectId || activeSourceId;
    if (!targetSourceId) return;
    const nextItems = asArray(aiDraftUpdate.items);
    lastAiDraftUpdateRef.current = aiDraftUpdate.version;
    if (nextItems.length === 0) return;

    setMapForSource(targetSourceId, (previous) => ({
      ...previous,
      items: ensureUniqueMapItemIds([
        ...asArray(previous.items).filter((item) => !isActiveEdge(item)),
        ...nextItems.map((item) => ({
          ...item,
          sourceObjectId: item?.sourceObjectId || targetSourceId,
          isActive: true,
          isDeleted: false,
        })),
      ]),
    }));
    setActiveSourceId(targetSourceId);
    setSelectedEdgeId(nextItems[0]?.id || null);
    scheduleAutoSave();
  }, [activeSourceId, aiDraftUpdate, scheduleAutoSave, setMapForSource]);

  const computeLayout = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scrollLeft = canvas.scrollLeft || 0;
    const scrollTop = canvas.scrollTop || 0;
    const nextLayout = {};
    const nextInputLayout = {};
    const nextAggregateLayout = {};
    const nextAggregateInputLayout = {};
    const nextLoopLayout = {};
    activeEdges.forEach((edge) => {
      const entityRef = entityRowRefs.current[edge.entityFieldId];
      if (!entityRef) return;
      const eRect = entityRef.getBoundingClientRect();
      const isConstant = edge?.isConstant === true || edge?.sourceFieldId === EMPTY_GUID;

      let sx;
      let sy;
      if (isConstant) {
        const gutterRect = gutterRef.current?.getBoundingClientRect();
        sx = (gutterRect ? (gutterRect.left + gutterRect.right) / 2 : rect.left + rect.width / 2) - rect.left + scrollLeft;
        sy = (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop;
      } else {
        const sourceRef = sourceRowRefs.current[edge.sourceFieldId];
        if (!sourceRef) return;
        const sRect = sourceRef.getBoundingClientRect();
        sx = sRect.right - rect.left + scrollLeft;
        sy = (sRect.top + sRect.bottom) / 2 - rect.top + scrollTop;
      }
      nextLayout[edge.id] = {
        sx,
        sy,
        tx: eRect.left - rect.left + scrollLeft,
        ty: (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop,
      };

      const inputLines = [];
      asArray(edge?.transformFields)
        .filter((input) => input?.isDeleted !== true && input?.isActive !== false && input?.sourceFieldId && input.sourceFieldId !== edge.sourceFieldId)
        .forEach((input) => {
          const sourceRef = sourceRowRefs.current[input.sourceFieldId];
          if (!sourceRef) return;
          const sRect = sourceRef.getBoundingClientRect();
          inputLines.push({
            id: input.id || `${edge.id}:${input.sourceFieldId}`,
            sourceFieldId: input.sourceFieldId,
            sx: sRect.right - rect.left + scrollLeft,
            sy: (sRect.top + sRect.bottom) / 2 - rect.top + scrollTop,
            tx: eRect.left - rect.left + scrollLeft,
            ty: (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop,
          });
        });
      if (inputLines.length > 0) nextInputLayout[edge.id] = inputLines;
    });
    activeAggregateEdges.forEach((aggregate) => {
      const entityRef = entityRowRefs.current[aggregate.targetEntityFieldId];
      if (!entityRef) return;
      const eRect = entityRef.getBoundingClientRect();
      let sourceRef = null;
      if (aggregate.inputSourceFieldId) {
        sourceRef = sourceRowRefs.current[aggregate.inputSourceFieldId];
      } else if (aggregate.inputEntityFieldId) {
        sourceRef = entityRowRefs.current[aggregate.inputEntityFieldId];
      }

      let sx;
      let sy;
      if (sourceRef) {
        const sRect = sourceRef.getBoundingClientRect();
        sx = sRect.right - rect.left + scrollLeft;
        sy = (sRect.top + sRect.bottom) / 2 - rect.top + scrollTop;
      } else {
        const gutterRect = gutterRef.current?.getBoundingClientRect();
        sx = (gutterRect ? (gutterRect.left + gutterRect.right) / 2 : rect.left + rect.width / 2) - rect.left + scrollLeft;
        sy = (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop;
      }

      nextAggregateLayout[aggregate.id] = {
        sx,
        sy,
        tx: eRect.left - rect.left + scrollLeft,
        ty: (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop,
      };

      const inputLines = [];
      asArray(aggregate?.transformFields)
        .filter((input) => input?.isDeleted !== true && input?.isActive !== false && input?.sourceFieldId && input.sourceFieldId !== aggregate.inputSourceFieldId)
        .forEach((input) => {
          const sourceRef = sourceRowRefs.current[input.sourceFieldId];
          if (!sourceRef) return;
          const sRect = sourceRef.getBoundingClientRect();
          inputLines.push({
            id: input.id || `${aggregate.id}:${input.sourceFieldId}`,
            sourceFieldId: input.sourceFieldId,
            sx: sRect.right - rect.left + scrollLeft,
            sy: (sRect.top + sRect.bottom) / 2 - rect.top + scrollTop,
            tx: eRect.left - rect.left + scrollLeft,
            ty: (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop,
          });
        });
      if (inputLines.length > 0) nextAggregateInputLayout[aggregate.id] = inputLines;
    });
    activeLoopEdges.forEach((loopEdge) => {
      const sourceRef = sourceRowRefs.current[loopEdge.sourceFieldId];
      if (!sourceRef) return;
      const sRect = sourceRef.getBoundingClientRect();
      const sx = sRect.right - rect.left + scrollLeft;
      const sy = (sRect.top + sRect.bottom) / 2 - rect.top + scrollTop;
      const gutterRect = gutterRef.current?.getBoundingClientRect();
      const chipX = (gutterRect ? (gutterRect.left + gutterRect.right) / 2 : rect.left + rect.width / 2) - rect.left + scrollLeft;
      const targets = asArray(loopEdge?.targetEntityFieldIds)
        .map((targetEntityFieldId) => {
          const entityRef = entityRowRefs.current[targetEntityFieldId];
          if (!entityRef) return null;
          const eRect = entityRef.getBoundingClientRect();
          return {
            id: targetEntityFieldId,
            tx: eRect.left - rect.left + scrollLeft,
            ty: (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop,
          };
        })
        .filter(Boolean);
      const targetAverageY = targets.length
        ? targets.reduce((sum, target) => sum + target.ty, 0) / targets.length
        : sy;
      nextLoopLayout[loopEdge.id] = {
        sx,
        sy,
        chipX,
        chipY: targets.length ? (sy + targetAverageY) / 2 : sy,
        targets,
      };
    });
    setLayout(nextLayout);
    setInputLayout(nextInputLayout);
    setAggregateLayout(nextAggregateLayout);
    setAggregateInputLayout(nextAggregateInputLayout);
    setLoopLayout(nextLoopLayout);
  }, [activeAggregateEdges, activeEdges, activeLoopEdges]);

  const openAggregateDialog = useCallback(
    (aggregateId) => {
      if (!aggregateId || !activeLoop) return;
      const aggregate = activeLoopAggregates.find((item) => item?.id === aggregateId);
      if (!aggregate) return;
      setSelectedAggregateId(aggregateId);
      setAggregateDialogTab("settings");
      setAggregateEditorValidation({
        preTransform: { canApply: true, warnings: [] },
        transform: { canApply: true, warnings: [] },
      });
      setAggregateDialog({
        loopId: activeLoop.id,
        aggregate: {
          ...aggregate,
          transformFields: asArray(aggregate?.transformFields).map((input) => ({ ...input })),
          preTransformSteps: asArray(aggregate?.preTransformSteps).map((step) => ({ ...step })),
          transformSteps: asArray(aggregate?.transformSteps).map((step) => ({ ...step })),
        },
      });
    },
    [activeLoop, activeLoopAggregates]
  );

  const patchAggregateDialogDraft = (patch) => {
    setAggregateDialog((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        aggregate: {
          ...(previous.aggregate || {}),
          ...(patch || {}),
        },
      };
    });
  };

  const updateAggregateEditorValidation = useCallback((key, state) => {
    setAggregateEditorValidation((previous) => {
      const warnings = asArray(state?.warnings);
      const nextState = {
        canApply: state?.canApply !== false,
        warnings,
      };
      const previousState = previous?.[key] || {};
      if (
        previousState.canApply === nextState.canApply &&
        JSON.stringify(previousState.warnings || []) === JSON.stringify(nextState.warnings)
      ) {
        return previous;
      }
      return {
        ...previous,
        [key]: nextState,
      };
    });
  }, []);

  const applyAggregateDialog = async () => {
    const loopId = aggregateDialog?.loopId;
    let draft = aggregateDialog?.aggregate;
    if (!loopId || !draft?.id) return { Success: false };
    if (aggregateDialogApplyWarnings.length > 0) {
      setAggregateDialogTab("settings");
      return { Success: false, Message: aggregateDialogApplyWarnings.join(" ") };
    }

    const preTransformResult = aggregatePreTransformEditorRef.current?.save
      ? await aggregatePreTransformEditorRef.current.save()
      : { Success: true };
    if (preTransformResult?.Success === false) {
      setAggregateDialogTab("preTransform");
      return preTransformResult;
    }

    const transformResult = aggregateTransformEditorRef.current?.save
      ? await aggregateTransformEditorRef.current.save()
      : { Success: true };
    if (transformResult?.Success === false) {
      setAggregateDialogTab("transform");
      return transformResult;
    }

    if (preTransformResult?.patch) {
      const preTransformSteps =
        preTransformResult.patch.multiTransformMode === false && String(preTransformResult.patch.legacyCustomTransformScript || preTransformResult.patch.transformCode || "").trim()
          ? [
              {
                id: `step_${Math.random().toString(36).slice(2)}`,
                kind: "custom",
                key: "custom",
                displayName: "Custom",
                code: String(preTransformResult.patch.legacyCustomTransformScript || preTransformResult.patch.transformCode || ""),
                isActive: true,
                isDeleted: false,
              },
            ]
          : asArray(preTransformResult.patch.transformSteps).map((step) => ({ ...(step || {}) }));
      draft = {
        ...draft,
        preTransformKey: preTransformResult.patch.transformKey || null,
        preTransformCode: preTransformResult.patch.transformCode || "",
        preTransformDisplayName: preTransformResult.patch.transformDisplayName || "",
        preTransformSteps,
        preTransformMultiMode: preTransformResult.patch.multiTransformMode !== false,
        transformFields: asArray(preTransformResult.patch.transformFields).map((input) => ({ ...(input || {}) })),
        ...(sourceFieldById.has(preTransformResult.patch.sourceFieldId)
          ? { inputSourceFieldId: preTransformResult.patch.sourceFieldId, inputEntityFieldId: null }
          : {}),
      };
    }

    if (transformResult?.patch) {
      const legacyCustomTransformScript =
        transformResult.patch.multiTransformMode === false
          ? String(transformResult.patch.legacyCustomTransformScript ?? transformResult.patch.transformCode ?? "")
          : "";
      draft = {
        ...draft,
        transformKey: transformResult.patch.transformKey || null,
        transformCode: transformResult.patch.transformCode || "",
        transformDisplayName: transformResult.patch.transformDisplayName || "",
        transformSteps: asArray(transformResult.patch.transformSteps).map((step) => ({ ...(step || {}) })),
        multiTransformMode: transformResult.patch.multiTransformMode !== false,
        legacyCustomTransformScript,
        finalTransformScript: legacyCustomTransformScript,
      };
    }

    setActiveLoops((previous) =>
      previous.map((loop) =>
        loop?.id === loopId
          ? {
              ...loop,
              aggregates: asArray(loop?.aggregates).map((aggregate) =>
                aggregate?.id === draft.id ? { ...aggregate, ...draft, isActive: true, isDeleted: false } : aggregate
              ),
            }
          : loop
      )
    );
    setSelectedAggregateId(draft.id);
    setAggregateDialog(null);
    setAggregateDialogTab("settings");
    scheduleAutoSave();
    return { Success: true };
  };

  const deleteAggregateById = useCallback(
    (aggregateId, loopId = activeLoop?.id) => {
      if (!aggregateId || !loopId) return;
      setActiveLoops((previous) =>
        previous.map((loop) =>
          loop?.id === loopId
            ? {
                ...loop,
                aggregates: asArray(loop?.aggregates).map((aggregate) =>
                  aggregate?.id === aggregateId ? { ...aggregate, isActive: false, isDeleted: true } : aggregate
                ),
              }
            : loop
        )
      );
      setSelectedAggregateId((previous) => (previous === aggregateId ? null : previous));
      setAggregateDialog((previous) => (previous?.aggregate?.id === aggregateId ? null : previous));
      scheduleAutoSave();
    },
    [activeLoop?.id, scheduleAutoSave, setActiveLoops]
  );

  const addSourceInputToExistingMapping = useCallback(
    (edgeId, sourceFieldId) => {
      const sourceField = sourceFieldById.get(sourceFieldId);
      if (!edgeId || !sourceField) return;
      setActiveMap((previous) => ({
        ...previous,
        items: asArray(previous.items).map((item) =>
          item?.id === edgeId
            ? addSourceInputToMappingItem(item, sourceField, activeSourceId, sourceFieldById, { includePrimary: true })
            : item
        ),
      }));
      setSelectedEdgeId(edgeId);
      scheduleAutoSave();
    },
    [activeSourceId, scheduleAutoSave, setActiveMap, sourceFieldById]
  );

  const addSourceInputToExistingAggregate = useCallback(
    (aggregateId, sourceFieldId) => {
      const sourceField = sourceFieldById.get(sourceFieldId);
      if (!activeLoop || !aggregateId || !sourceField) return;
      setActiveLoops((previous) =>
        previous.map((loop) =>
          loop?.id === activeLoop.id
            ? {
                ...loop,
                aggregates: asArray(loop?.aggregates).map((aggregate) =>
                  aggregate?.id === aggregateId
                    ? addSourceInputToMappingItem(aggregate, sourceField, activeSourceId, sourceFieldById, { includePrimary: false })
                    : aggregate
                ),
              }
            : loop
        )
      );
      setSelectedAggregateId(aggregateId);
      scheduleAutoSave();
    },
    [activeLoop, activeSourceId, scheduleAutoSave, setActiveLoops, sourceFieldById]
  );

  const createAggregateMapping = useCallback(
    ({ inputSourceFieldId = null, inputEntityFieldId = null, targetEntityFieldId = null }) => {
      if (!activeLoop || !targetEntityFieldId || (!inputSourceFieldId && !inputEntityFieldId)) return;
      if (inputEntityFieldId && inputEntityFieldId === targetEntityFieldId) return;
      if (inputEntityFieldId && !activeEdges.some((edge) => edge?.entityFieldId === inputEntityFieldId)) return;

      const existingForTarget = activeLoopAggregates.find((aggregate) => aggregate?.targetEntityFieldId === targetEntityFieldId);
      if (inputSourceFieldId && existingForTarget) {
        addSourceInputToExistingAggregate(existingForTarget.id, inputSourceFieldId);
        return;
      }

      const existing = activeLoopAggregates.find(
        (aggregate) =>
          aggregate?.targetEntityFieldId === targetEntityFieldId &&
          (aggregate?.inputSourceFieldId || null) === (inputSourceFieldId || null) &&
          (aggregate?.inputEntityFieldId || null) === (inputEntityFieldId || null)
      );
      const nextAggregate = {
        ...(existing || {}),
        id: existing?.id || `aggregate_${Math.random().toString(36).slice(2)}`,
        targetEntityFieldId,
        reducer: existing?.reducer || "Sum",
        inputEntityFieldId: inputEntityFieldId || null,
        inputSourceFieldId: inputSourceFieldId || null,
        inputPath: "",
        transformFields: asArray(existing?.transformFields),
        separator: existing?.separator || ",",
        finalTransformScript: existing?.finalTransformScript || "",
        preTransformKey: existing?.preTransformKey || null,
        preTransformCode: existing?.preTransformCode || "",
        preTransformDisplayName: existing?.preTransformDisplayName || "",
        preTransformSteps: asArray(existing?.preTransformSteps),
        preTransformMultiMode: existing?.preTransformMultiMode !== false,
        transformKey: existing?.transformKey || null,
        transformCode: existing?.transformCode || "",
        transformDisplayName: existing?.transformDisplayName || "",
        transformSteps: asArray(existing?.transformSteps),
        multiTransformMode: existing?.multiTransformMode !== false,
        legacyCustomTransformScript: existing?.legacyCustomTransformScript || existing?.finalTransformScript || "",
        isActive: true,
        isDeleted: false,
      };

      setActiveLoops((previous) =>
        previous.map((loop) => {
          if (loop?.id !== activeLoop.id) return loop;
          const aggregates = asArray(loop?.aggregates);
          const found = aggregates.some((aggregate) => aggregate?.id === nextAggregate.id);
          return {
            ...loop,
            aggregates: found
              ? aggregates.map((aggregate) => (aggregate?.id === nextAggregate.id ? nextAggregate : aggregate))
              : [...aggregates, nextAggregate],
          };
        })
      );
      setSelectedAggregateId(nextAggregate.id);
      setAggregateDialogTab("settings");
      setAggregateEditorValidation({
        preTransform: { canApply: true, warnings: [] },
        transform: { canApply: true, warnings: [] },
      });
      setAggregateDialog({
        loopId: activeLoop.id,
        aggregate: { ...nextAggregate },
      });
      scheduleAutoSave();
    },
    [activeEdges, activeLoop, activeLoopAggregates, addSourceInputToExistingAggregate, scheduleAutoSave, setActiveLoops]
  );

  const openCollectionLoopDialog = useCallback(
    ({ sourceFieldId, targetEntityFieldId = null }) => {
      const sourceField = sourceFieldById.get(sourceFieldId);
      if (!activeSourceId || !sourceField || !isSourceCollectionField(sourceField)) return false;
      const targetField = targetEntityFieldId ? entityFieldById.get(targetEntityFieldId) : null;
      const targetContainerId = targetField && isTargetContainerField(targetField) ? targetEntityFieldId : null;
      const existingIndex = activeVisibleLoops.findIndex(
        (loop) =>
          getEffectiveLoopSourceMode(loop) === "InlineCollection" &&
          loop?.collectionSourceFieldId === sourceFieldId &&
          (loop?.targetContainerEntityObjectFieldId || null) === (targetContainerId || null)
      );
      const existingLoop = existingIndex >= 0 ? activeVisibleLoops[existingIndex] : null;
      const aliasNumber = existingLoop ? getLoopNumberFromAlias(existingLoop) || getNextLoopNumber() : getNextLoopNumber();
      const loopNumber = existingLoop
        ? loopDisplayNumberById.get(existingLoop.id) || getLoopModeDisplayNumber(activeVisibleLoops, existingLoop, existingIndex + 1)
        : getNextLoopDisplayNumber("InlineCollection");
      const collectionName = sourceField?.displayName || sourceField?.fieldName || getLoopDefaultName(loopNumber);
      const draft = existingLoop
        ? cloneLoops([existingLoop])[0]
        : buildEmptyLoopMap(entityObject?.id, activeSourceId, {
            name: collectionName,
            loopAlias: `loop${aliasNumber}`,
          });
      const nextLoop = {
        ...draft,
        name: String(draft?.name || collectionName || getLoopDefaultName(loopNumber)).trim(),
        loopAlias: String(draft?.loopAlias || `loop${aliasNumber}`).trim() || `loop${aliasNumber}`,
        entityObjectId: draft?.entityObjectId || entityObject?.id,
        sourceObjectId: draft?.sourceObjectId || activeSourceId,
        loopSourceMode: "InlineCollection",
        collectionSourceFieldId: sourceFieldId,
        collectionPath: getCollectionPathForField(sourceField),
        groupBySourceFieldId: null,
        sortSourceFieldId: null,
        targetMode: targetContainerId ? "NestedContainer" : "FlatRows",
        targetContainerEntityObjectFieldId: targetContainerId,
        suppressAutoTargetContainer: !targetContainerId,
        isActive: true,
        isDeleted: false,
      };

      setActiveUtilityPanel("loops");
      setLoopCanvasMode("map");
      setLoopDialog({
        mode: existingLoop ? "edit" : "add",
        loop: nextLoop,
        loopNumber,
        lockCollectionSource: true,
      });
      return true;
    },
    [activeSourceId, activeVisibleLoops, entityFieldById, entityObject?.id, getNextLoopDisplayNumber, getNextLoopNumber, loopDisplayNumberById, sourceFieldById]
  );

  useEffect(() => {
    computeLayout();
    const handleResize = () => computeLayout();
    const canvas = canvasRef.current;
    const handleScroll = () => computeLayout();
    window.addEventListener("resize", handleResize);
    if (canvas) canvas.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (canvas) canvas.removeEventListener("scroll", handleScroll);
    };
  }, [computeLayout]);

  const beginSourceDrag = (field, event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const sourceRef = sourceRowRefs.current[field.id];
    if (!canvas || !sourceRef) return;
    const canvasRect = canvas.getBoundingClientRect();
    const scrollLeft = canvas.scrollLeft || 0;
    const scrollTop = canvas.scrollTop || 0;
    const sRect = sourceRef.getBoundingClientRect();
    const sx = sRect.right - canvasRect.left + scrollLeft;
    const sy = (sRect.top + sRect.bottom) / 2 - canvasRect.top + scrollTop;
    setDragging({
      mode: isLoopAggregateMode ? "aggregate-source" : "source",
      sourceFieldId: field.id,
      isLoop: !isLoopAggregateMode && isSourceCollectionField(field),
      sx,
      sy,
      tx: sx,
      ty: sy,
    });
  };

  const beginEntityDrag = (field, event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const entityRef = entityRowRefs.current[field.id];
    if (!canvas || !entityRef) return;
    const canvasRect = canvas.getBoundingClientRect();
    const scrollLeft = canvas.scrollLeft || 0;
    const scrollTop = canvas.scrollTop || 0;
    const eRect = entityRef.getBoundingClientRect();
    const sx = eRect.left - canvasRect.left + scrollLeft;
    const sy = (eRect.top + eRect.bottom) / 2 - canvasRect.top + scrollTop;
    setDragging({
      mode: isLoopAggregateMode ? "aggregate-entity" : "entity",
      entityFieldId: field.id,
      isLoop: !isLoopAggregateMode && isTargetContainerField(field),
      sx,
      sy,
      tx: sx,
      ty: sy,
    });
  };

  useEffect(() => {
    const onMove = (event) => {
      if (!dragging || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const canvasRect = canvas.getBoundingClientRect();

      // Auto-scroll upward while dragging near the top edge.
      const EDGE_THRESHOLD = 52;
      const MAX_STEP = 18;
      const pointerY = event.clientY - canvasRect.top;
      if (pointerY < EDGE_THRESHOLD && canvas.scrollTop > 0) {
        const ratio = (EDGE_THRESHOLD - Math.max(pointerY, 0)) / EDGE_THRESHOLD;
        const step = Math.max(2, Math.round(MAX_STEP * ratio));
        canvas.scrollTop = Math.max(0, canvas.scrollTop - step);
      }

      const scrollLeft = canvas.scrollLeft || 0;
      const scrollTop = canvas.scrollTop || 0;
      setDragging((previous) => ({
        ...previous,
        tx: event.clientX - canvasRect.left + scrollLeft,
        ty: event.clientY - canvasRect.top + scrollTop,
      }));
    };
    const onUp = (event) => {
      if (!dragging) return;
      const dropTarget = getCanvasDropTarget(event);

      if (dragging.mode === "aggregate-source" || dragging.mode === "aggregate-entity") {
        if (dragging.mode === "aggregate-source" && dropTarget.aggregateId) {
          addSourceInputToExistingAggregate(dropTarget.aggregateId, dragging.sourceFieldId);
          setDragging(null);
          return;
        }

        let targetEntityId = null;
        Object.keys(entityRowRefs.current).forEach((id) => {
          const element = entityRowRefs.current[id];
          if (!element) return;
          const rect = element.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
            targetEntityId = id;
          }
        });
        targetEntityId = targetEntityId || dropTarget.targetEntityFieldId;
        if (targetEntityId) {
          createAggregateMapping({
            inputSourceFieldId: dragging.mode === "aggregate-source" ? dragging.sourceFieldId : null,
            inputEntityFieldId: dragging.mode === "aggregate-entity" ? dragging.entityFieldId : null,
            targetEntityFieldId: targetEntityId,
          });
        }
      } else if (dragging.mode === "source") {
        let targetEntityId = null;
        Object.keys(entityRowRefs.current).forEach((id) => {
          const element = entityRowRefs.current[id];
          if (!element) return;
          const rect = element.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
            targetEntityId = id;
          }
        });
        targetEntityId = targetEntityId || dropTarget.targetEntityFieldId;
        if (targetEntityId) {
          const sourceField = sourceFieldById.get(dragging.sourceFieldId);
          const targetField = entityFieldById.get(targetEntityId);
          if (isSourceCollectionField(sourceField)) {
            openCollectionLoopDialog({
              sourceFieldId: dragging.sourceFieldId,
              targetEntityFieldId: isTargetContainerField(targetField) ? targetEntityId : null,
            });
            setDragging(null);
            return;
          }

          if (dropTarget.mapEdgeId) {
            addSourceInputToExistingMapping(dropTarget.mapEdgeId, dragging.sourceFieldId);
            setDragging(null);
            return;
          }

          const existingTargetEdge = activeEdges.find(
            (edge) => edge?.entityFieldId === targetEntityId && edge?.isConstant !== true && edge?.sourceFieldId !== EMPTY_GUID
          );
          if (existingTargetEdge && existingTargetEdge.sourceFieldId !== dragging.sourceFieldId) {
            addSourceInputToExistingMapping(existingTargetEdge.id, dragging.sourceFieldId);
            setDragging(null);
            return;
          }

          const nextItem = {
            id: `item_${Math.random().toString(36).slice(2)}`,
            sourceFieldId: dragging.sourceFieldId,
            sourceObjectId: activeSourceId,
            loopMapId: activeScopeLoopId,
            entityFieldId: targetEntityId,
            transformKey: null,
            transformCode: "",
            transformDisplayName: "",
            importance: 255,
            isConstant: false,
            isActive: true,
            isDeleted: false,
          };
          setActiveMap((previous) => ({
            ...previous,
            items: replaceMapping(previous.items, nextItem, { allowMultipleEntitiesPerSourceField: true }),
          }));
          setSelectedEdgeId(nextItem.id);
          scheduleAutoSave();
        }
      } else if (dragging.mode === "entity") {
        let targetSourceId = null;
        Object.keys(sourceRowRefs.current).forEach((id) => {
          const element = sourceRowRefs.current[id];
          if (!element) return;
          const rect = element.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
            targetSourceId = id;
          }
        });
        if (targetSourceId) {
          const sourceField = sourceFieldById.get(targetSourceId);
          const entityField = entityFieldById.get(dragging.entityFieldId);
          if (isSourceCollectionField(sourceField)) {
            openCollectionLoopDialog({
              sourceFieldId: targetSourceId,
              targetEntityFieldId: isTargetContainerField(entityField) ? dragging.entityFieldId : null,
            });
            setDragging(null);
            return;
          }

          const existingTargetEdge = activeEdges.find(
            (edge) => edge?.entityFieldId === dragging.entityFieldId && edge?.isConstant !== true && edge?.sourceFieldId !== EMPTY_GUID
          );
          if (existingTargetEdge && existingTargetEdge.sourceFieldId !== targetSourceId) {
            addSourceInputToExistingMapping(existingTargetEdge.id, targetSourceId);
            setDragging(null);
            return;
          }

          const nextItem = {
            id: `item_${Math.random().toString(36).slice(2)}`,
            sourceFieldId: targetSourceId,
            sourceObjectId: activeSourceId,
            loopMapId: activeScopeLoopId,
            entityFieldId: dragging.entityFieldId,
            transformKey: null,
            transformCode: "",
            transformDisplayName: "",
            importance: 255,
            isConstant: false,
            isActive: true,
            isDeleted: false,
          };
          setActiveMap((previous) => ({
            ...previous,
            items: replaceMapping(previous.items, nextItem, { allowMultipleEntitiesPerSourceField: true }),
          }));
          setSelectedEdgeId(nextItem.id);
          scheduleAutoSave();
          setDragging(null);
          return;
        }

        const gutterRect = gutterRef.current?.getBoundingClientRect();
        const droppedInGutter = !!(
          gutterRect &&
          event.clientX >= gutterRect.left &&
          event.clientX <= gutterRect.right &&
          event.clientY >= gutterRect.top &&
          event.clientY <= gutterRect.bottom
        );
        if (droppedInGutter) {
          const nextItem = {
            id: `item_${Math.random().toString(36).slice(2)}`,
            sourceFieldId: EMPTY_GUID,
            sourceObjectId: activeSourceId,
            loopMapId: activeScopeLoopId,
            entityFieldId: dragging.entityFieldId,
            transformKey: null,
            transformCode: "",
            transformDisplayName: "Constant",
            importance: 255,
            isConstant: true,
            isActive: true,
            isDeleted: false,
          };
          setActiveMap((previous) => ({
            ...previous,
            items: replaceMapping(previous.items, nextItem, { allowMultipleEntitiesPerSourceField: true }),
          }));
          setSelectedEdgeId(nextItem.id);
          scheduleAutoSave();
        }
      }
      setDragging(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [activeEdges, activeScopeLoopId, activeSourceId, addSourceInputToExistingAggregate, addSourceInputToExistingMapping, createAggregateMapping, dragging, entityFieldById, openCollectionLoopDialog, setActiveMap, scheduleAutoSave, sourceFieldById]);

  useEffect(() => {
    computeLayout();
  }, [computeLayout, visibleSourceFields, visibleEntityFields, activeEdges.length, activeAggregateEdges.length, activeLoopEdges.length]);

  const handleDeleteEdge = (edgeId) => {
    setActiveMap((previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) => {
        if (item.id !== edgeId) return item;
        return {
          ...item,
          isActive: false,
          isDeleted: false,
        };
      }),
    }));
    setSelectedEdgeId(null);
    setTransformEdgeId((previous) => (previous === edgeId ? null : previous));
    scheduleAutoSave();
  };

  const handleActivateSearchMapping = () => {
    if (!selectedSearchMapping || !selectedSearchSourceId) return;
    setMapForSource(selectedSearchSourceId, (previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) => {
        const conflicts =
          item.id !== selectedSearchMapping.id &&
          isActiveEdge(item) &&
          (item?.loopMapId || null) === (selectedSearchMapping?.loopMapId || null) &&
          (
            item.entityFieldId === selectedSearchMapping.entityFieldId ||
            (!selectedSearchMapping.isConstant && item.sourceFieldId === selectedSearchMapping.sourceFieldId)
          );
        if (conflicts) {
          return {
            ...item,
            isActive: false,
            isDeleted: false,
          };
        }
        if (item.id !== selectedSearchMapping.id) return item;
        return {
          ...item,
          isActive: true,
          isDeleted: false,
        };
      }),
    }));
    setActiveSourceId(selectedSearchSourceId);
    setSelectedSearchEdgeId(selectedSearchMapping.id);
    setSelectedEdgeId(selectedSearchMapping.id);
    scheduleAutoSave();
  };

  const handleOpenSearchMapping = () => {
    if (!selectedSearchMapping || !isActiveEdge(selectedSearchMapping) || !selectedSearchSourceId) return;
    if (activeSourceId !== selectedSearchSourceId) {
      setPendingOpenMapping({ sourceId: selectedSearchSourceId, edgeId: selectedSearchMapping.id });
      setActiveSourceId(selectedSearchSourceId);
      return;
    }
    setSelectedEdgeId(selectedSearchMapping.id);
    setTransformEdgeId(selectedSearchMapping.id);
  };

  const handleSelectSearchMapping = (value) => {
    const nextId = value || "";
    setSelectedSearchEdgeId(nextId);
    if (!nextId) {
      setSelectedEdgeId(null);
      return;
    }
    const edge = searchMappingItems.find((item) => item.id === nextId) || null;
    if (!edge || !isActiveEdge(edge)) {
      setSelectedEdgeId(null);
      return;
    }
    if (activeSourceId !== selectedSearchSourceId) {
      setPendingSelectMapping({ sourceId: selectedSearchSourceId, edgeId: edge.id });
      setActiveSourceId(selectedSearchSourceId);
      return;
    }
    setSelectedEdgeId(edge.id);
  };

  const handleTransformSave = (patch) => {
    setActiveMap((previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) => (item.id === transformEdgeId ? { ...item, ...patch } : item)),
    }));
    setTransformEdgeId(null);
    scheduleAutoSave();
  };

  const rollback = () => {
    const saved = savedBySourceId[activeSourceId];
    if (saved) {
      setMapsBySourceId((previous) => ({ ...previous, [activeSourceId]: cloneMap(saved) }));
      const key = draftKey(entityObject?.id, activeSourceId, saved.id || "new");
      localStorage.removeItem(key);
    }
    setLoopMapsBySourceId((previous) => ({ ...previous, [activeSourceId]: cloneLoops(savedLoopMapsBySourceId[activeSourceId] || []) }));
    localStorage.removeItem(loopDraftKey(entityObject?.id, activeSourceId));
    setActiveLoopId(null);
    setSelectedEdgeId(null);
    setTransformEdgeId(null);
    setAggregateDialogTab("settings");
    setDragging(null);
    setEntityFields((previous) => reorderByIdList(cloneFields(savedEntityFields), savedEntityFieldOrder.length > 0 ? savedEntityFieldOrder : previous.map((x) => x.id)));
    setEntityObjectDraftRaw(savedEntityObjectRaw || {});
    setPendingFieldPatches({});
    setDraftRestored(false);
  };

  const clearCurrentMap = () => {
    setActiveMap((previous) => ({
      ...previous,
      items: asArray(previous.items).filter((item) => (item?.loopMapId || null) !== activeScopeLoopId),
    }));
    setSelectedEdgeId(null);
    setTransformEdgeId(null);
    setActiveUtilityPanel((previous) => (previous === "search" ? null : previous));
    setDragging(null);
    scheduleAutoSave();
  };

  const handleSaveEntityField = async (patch) => {
    const fieldId = editField?.id;
    if (!fieldId) return { Success: false };
    const normalizedPatch = normalizeField({ ...(editField?.raw || {}), ...(patch || {}) });
    setEntityFields((previous) =>
      previous.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              ...normalizedPatch,
              id: field.id,
              raw: { ...(field.raw || {}), ...(patch || {}) },
            }
          : field
      )
    );
    setPendingFieldPatches((previous) => ({ ...previous, [fieldId]: { ...(previous[fieldId] || {}), ...(patch || {}) } }));
    setEditField(null);
    return { Success: true };
  };

  const applyAiSuggestion = async () => {
    if (!aiConfirm) return { Success: true };
    setActiveMap((previous) => ({
      ...previous,
        items: [
          ...asArray(previous.items).filter((item) => !isActiveEdge(item)),
          ...aiConfirm.items.map((item) => ({
            ...item,
            sourceObjectId: item?.sourceObjectId || activeSourceId,
            isActive: true,
            isDeleted: false,
          })),
        ],
      }));
    setAiConfirm(null);
    return { Success: true };
  };

  const runAiForField = async (sourceField) => {
    setAiFieldLoadingTarget(sourceField?.id ? { side: "source", id: sourceField.id } : null);
    try {
      const result = await onAiSuggestForField({
        entityObject,
        sourceObject: activeSource,
        sourceField,
        currentMap: activeMap,
      });
      if (!result?.Success) {
        setAiConfirm({
          items: activeEdges,
          summary: { added: 0, removed: 0, replaced: 0 },
          warnings: [String(result?.error || result?.DisplayMessage || "AI suggestion failed.")],
          canApprove: false,
        });
        return;
      }
      const suggested = asArray(result.items);
      const summary = mapDiff(activeEdges, suggested);
      const warnings = suggested
        .filter((edge) => edgeInvalid(edge, sourceFieldById, entityFieldById, validationLookupContext))
        .map((edge) => {
          const sf = sourceFieldById.get(edge.sourceFieldId);
          const ef = entityFieldById.get(edge.entityFieldId);
          return `${sf?.displayName || sf?.fieldName} -> ${ef?.displayName || ef?.fieldName} requires transform`;
        });
      setAiConfirm({ items: suggested, summary, warnings: [...asArray(result?.warnings), ...warnings] });
    } finally {
      setAiFieldLoadingTarget(null);
    }
  };

  const runAiForEntityField = async (entityField) => {
    setAiFieldLoadingTarget(entityField?.id ? { side: "entity", id: entityField.id } : null);
    try {
      const result = await onAiSuggestForEntityField({
        entityObject,
        sourceObject: activeSource,
        entityField,
        currentMap: activeMap,
      });
      if (!result?.Success) {
        setAiConfirm({
          items: activeEdges,
          summary: { added: 0, removed: 0, replaced: 0 },
          warnings: [String(result?.error || result?.DisplayMessage || "AI suggestion failed.")],
          canApprove: false,
        });
        return;
      }

      const suggested = asArray(result.items);
      const summary = mapDiff(activeEdges, suggested);
      const warnings = suggested
        .filter((edge) => edgeInvalid(edge, sourceFieldById, entityFieldById, validationLookupContext))
        .map((edge) => {
          const sf = sourceFieldById.get(edge.sourceFieldId);
          const ef = entityFieldById.get(edge.entityFieldId);
          return `${sf?.displayName || sf?.fieldName} -> ${ef?.displayName || ef?.fieldName} requires transform`;
        });
      setAiConfirm({ items: suggested, summary, warnings: [...asArray(result?.warnings), ...warnings] });
    } finally {
      setAiFieldLoadingTarget(null);
    }
  };

  const selectedEdge = activeEdges.find((x) => x.id === transformEdgeId) || null;
  const aggregateDialogDraft = aggregateDialog?.aggregate || null;
  const aggregateDialogInputField = useMemo(() => {
    if (!aggregateDialogDraft) return "";
    const sourceField = sourceFieldById.get(aggregateDialogDraft?.inputSourceFieldId);
    const entityField = entityFieldById.get(aggregateDialogDraft?.inputEntityFieldId);
    return sourceField || entityField || null;
  }, [aggregateDialogDraft, entityFieldById, sourceFieldById]);
  const aggregateDialogInputFallback = useMemo(() => {
    if (!aggregateDialogDraft) return "Not set";
    return String(aggregateDialogDraft?.inputPath || "").trim() || "Not set";
  }, [aggregateDialogDraft]);
  const aggregateDialogTargetField = useMemo(() => {
    return entityFieldById.get(aggregateDialogDraft?.targetEntityFieldId) || null;
  }, [aggregateDialogDraft, entityFieldById]);
  const aggregateReducerInputField = useMemo(() => ({
    id: "aggregate-reducer-input",
    fieldName: "ReducerInput",
    displayName: "Reducer Input",
    dataTypeId: aggregateDialogInputField?.dataTypeId || aggregateDialogTargetField?.dataTypeId || "",
    dataTypeName: aggregateDialogInputField?.dataTypeName || aggregateDialogTargetField?.dataTypeName || "",
  }), [aggregateDialogInputField, aggregateDialogTargetField]);
  const aggregateTransformResultField = useMemo(() => ({
    id: "aggregate-transform-result",
    fieldName: "TransformResult",
    displayName: "Transform Result",
    dataTypeId: aggregateDialogInputField?.dataTypeId || aggregateDialogTargetField?.dataTypeId || "",
    dataTypeName: aggregateDialogInputField?.dataTypeName || aggregateDialogTargetField?.dataTypeName || "",
  }), [aggregateDialogInputField, aggregateDialogTargetField]);
  const aggregateReducerResultField = useMemo(() => ({
    id: "aggregate-reducer-result",
    fieldName: "ReducerResult",
    displayName: "Reducer Result",
    dataTypeId: aggregateDialogTargetField?.dataTypeId || "",
    dataTypeName: aggregateDialogTargetField?.dataTypeName || "",
  }), [aggregateDialogTargetField]);
  const aggregateDialogInputSummary = useMemo(() => {
    if (!aggregateDialogDraft) return "";
    const labels = [];
    const primaryLabel = getFieldSummaryLabel(aggregateDialogInputField, aggregateDialogInputFallback);
    if (primaryLabel && primaryLabel !== "Not set") labels.push(primaryLabel);
    asArray(aggregateDialogDraft?.transformFields)
      .filter((input) => input?.sourceFieldId && input?.isDeleted !== true && input?.isActive !== false)
      .forEach((input) => {
        const field = sourceFieldById.get(input.sourceFieldId);
        const label = String(getFieldSummaryLabel(field, input?.displayName || input?.inputName || input?.fieldName) || "").trim();
        if (label) labels.push(label);
      });
    const seen = new Set();
    return labels
      .filter((label) => {
        const key = label.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .join(", ");
  }, [aggregateDialogDraft, aggregateDialogInputFallback, aggregateDialogInputField, sourceFieldById]);
  const aggregateDialogWarnings = useMemo(() => {
    if (!aggregateDialogDraft) return [];
    return getAggregateValidationWarnings(aggregateDialogDraft, {
      ...validationLookupContext,
      sourceFieldById,
      entityFieldById,
      activeEdges,
    });
  }, [activeEdges, aggregateDialogDraft, entityFieldById, sourceFieldById, validationLookupContext]);
  const aggregateEditorWarnings = useMemo(() => {
    const warnings = [];
    if (aggregateEditorValidation.preTransform?.canApply === false) warnings.push("Pre Transform tab has invalid configuration.");
    if (aggregateEditorValidation.transform?.canApply === false) warnings.push("Transform tab has invalid configuration.");
    return warnings;
  }, [aggregateEditorValidation]);
  const aggregateDialogApplyWarnings = useMemo(
    () => [...aggregateDialogWarnings, ...aggregateEditorWarnings],
    [aggregateDialogWarnings, aggregateEditorWarnings]
  );
  const aggregatePreTransformMapping = useMemo(() => {
    if (!aggregateDialogDraft) return null;
    return {
      ...aggregateDialogDraft,
      sourceObjectId: activeSourceId,
      sourceFieldId: aggregateDialogDraft?.inputSourceFieldId || aggregateDialogDraft?.inputEntityFieldId || "",
      entityFieldId: aggregateReducerInputField.id,
      transformSteps: asArray(aggregateDialogDraft?.preTransformSteps),
      transformKey: aggregateDialogDraft?.preTransformKey || null,
      transformCode: aggregateDialogDraft?.preTransformCode || "",
      transformDisplayName: aggregateDialogDraft?.preTransformDisplayName || "",
      transformFields: asArray(aggregateDialogDraft?.transformFields),
      multiTransformMode: aggregateDialogDraft?.preTransformMultiMode !== false,
      legacyCustomTransformScript: "",
      importance: 255,
      isConstant: false,
      raw: {},
    };
  }, [activeSourceId, aggregateDialogDraft, aggregateReducerInputField.id]);
  const aggregatePostTransformMapping = useMemo(() => {
    if (!aggregateDialogDraft) return null;
    const legacyCustomTransformScript =
      aggregateDialogDraft.multiTransformMode === false
        ? aggregateDialogDraft.legacyCustomTransformScript || aggregateDialogDraft.finalTransformScript || ""
        : "";
    return {
      ...aggregateDialogDraft,
      sourceObjectId: activeSourceId,
      sourceFieldId: aggregateDialogDraft?.inputSourceFieldId || aggregateDialogDraft?.inputEntityFieldId || "",
      entityFieldId: aggregateDialogDraft?.targetEntityFieldId || "",
      transformSteps: asArray(aggregateDialogDraft?.transformSteps),
      transformKey: aggregateDialogDraft?.transformKey || null,
      transformCode: aggregateDialogDraft?.transformCode || legacyCustomTransformScript || "",
      transformDisplayName: aggregateDialogDraft?.transformDisplayName || "",
      transformFields: [],
      multiTransformMode: aggregateDialogDraft?.multiTransformMode !== false,
      legacyCustomTransformScript,
      importance: 255,
      isConstant: false,
      raw: {
        CustomTransformScript: legacyCustomTransformScript,
      },
    };
  }, [activeSourceId, aggregateDialogDraft]);
  const loopDialogDraft = loopDialog?.loop || null;
  const loopDialogSourceMode = getEffectiveLoopSourceMode(loopDialogDraft);
  const loopDialogUsesGroupedRows = loopDialogSourceMode === "GroupedRows";
  const loopDialogLocksCollectionSource = loopDialog?.lockCollectionSource === true;
  const loopDialogValidation = useMemo(() => {
    return getLoopValidation(loopDialogDraft, {
      sourceFields: activeSource?.fields,
      sourceObjectId: activeSourceId,
      entityFieldById,
    });
  }, [activeSource?.fields, activeSourceId, entityFieldById, loopDialogDraft]);
  const loopDialogWarnings = loopDialogValidation.warnings;
  const loopDialogInvalidFields = loopDialogValidation.invalidFields;
  const deleteLoopMappingCount = useMemo(
    () =>
      deleteLoopConfirm?.id
        ? asArray(activeMap?.items).filter((item) => item?.loopMapId === deleteLoopConfirm.id && isActiveEdge(item)).length
        : 0,
    [activeMap?.items, deleteLoopConfirm]
  );
  const loopFieldOptions = useMemo(
    () =>
      asArray(activeSource?.fields).map((field) => ({
        value: field.id,
        label: getFieldOptionLabel(field),
      })),
    [activeSource]
  );
  const collectionFieldOptions = useMemo(
    () =>
      asArray(activeSource?.fields)
        .filter(isSourceCollectionField)
        .map((field) => ({
          value: field.id,
          label: getFieldOptionLabel(field),
          path: getCollectionPathForField(field),
        })),
    [activeSource]
  );
  const collectionFieldById = useMemo(() => new Map(collectionFieldOptions.map((field) => [field.value, field])), [collectionFieldOptions]);
  const getCollectionPathFromFieldId = useCallback(
    (fieldId) => collectionFieldById.get(fieldId)?.path || "",
    [collectionFieldById]
  );
  const loopDialogShowsCollectionPath =
    loopDialogSourceMode === "InlineCollection" &&
    !loopDialogDraft?.collectionSourceFieldId &&
    (collectionFieldOptions.length === 0 || !!String(loopDialogDraft?.collectionPath || "").trim());
  const entityFieldOptions = useMemo(
    () =>
      asArray(entityFields).map((field) => ({
        value: field.id,
        label: getFieldOptionLabel(field),
      })),
    [entityFields]
  );
  const targetContainerFieldOptions = useMemo(
    () =>
      asArray(entityFields)
        .filter(isTargetContainerField)
        .map((field) => ({
          value: field.id,
          label: getFieldOptionLabel(field),
        })),
    [entityFields]
  );
  const defaultTargetContainerId = targetContainerFieldOptions[0]?.value || null;
  const autoTargetContainerId = targetContainerFieldOptions.length === 1 ? targetContainerFieldOptions[0].value : null;
  const loopDialogShowsTargetContainer = loopDialogSourceMode !== "GroupedRows" && targetContainerFieldOptions.length > 1;
  const loopFieldControlOptions = useMemo(() => toFieldControlOptions(loopFieldOptions), [loopFieldOptions]);
  const collectionFieldControlOptions = useMemo(() => toFieldControlOptions(collectionFieldOptions), [collectionFieldOptions]);
  const targetContainerFieldControlOptions = useMemo(() => toFieldControlOptions(targetContainerFieldOptions), [targetContainerFieldOptions]);
  const aggregateReducerControlOptions = useMemo(() => toFieldControlOptions(LOOP_AGGREGATE_REDUCERS), []);
  const renderLoopFieldControl = (fieldMetadata, value, onChange, help, options = {}) => (
    <div className={`efmd-loop-dialog-field${options.invalid ? " is-invalid" : ""}${options.className ? ` ${options.className}` : ""}`}>
      <FieldControl
        className="efmd-loop-dialog-control"
        fieldMetadata={{
          ...(fieldMetadata || {}),
          ...(help ? { Description: help } : {}),
        }}
        updatedItem={options.updatedItem || loopDialogDraft || {}}
        value={value}
        onChange={onChange}
        disabled={options.disabled}
        disableValidation={true}
        disableError={true}
      />
    </div>
  );

  const openAddLoopDialog = () => {
    const aliasNumber = getNextLoopNumber();
    const loopNumber = getNextLoopDisplayNumber("GroupedRows");
    const next = buildEmptyLoopMap(entityObject?.id, activeSourceId, {
      name: getLoopDefaultName(loopNumber, "GroupedRows"),
      loopAlias: `loop${aliasNumber}`,
      targetMode: "FlatRows",
      targetContainerEntityObjectFieldId: null,
    });
    setLoopDialog({ mode: "add", loop: next, loopNumber });
  };

  const openEditLoopDialog = (loop, loopNumber) => {
    if (!loop) return;
    const draft = cloneLoops([loop])[0];
    const displayNumber = loopNumber || getLoopModeDisplayNumber(activeVisibleLoops, loop, 1);
    if (isGeneratedLoopDisplayName(draft.name)) {
      draft.name = getLoopDefaultName(displayNumber, draft);
    }
    const draftUsesGroupedRows = getEffectiveLoopSourceMode(draft) === "GroupedRows";
    if (draftUsesGroupedRows) {
      draft.targetContainerEntityObjectFieldId = null;
      draft.targetMode = "FlatRows";
    }
    const initialTargetContainerId = draftUsesGroupedRows ? null : autoTargetContainerId || (loopDialogShowsTargetContainer ? defaultTargetContainerId : null);
    if (!draft.targetContainerEntityObjectFieldId && initialTargetContainerId && draft.targetMode !== "FlatRows") {
      draft.targetContainerEntityObjectFieldId = initialTargetContainerId;
      draft.targetMode = "NestedContainer";
    }
    setLoopDialog({
      mode: "edit",
      loop: draft,
      loopNumber: displayNumber,
    });
  };

  const patchLoopDialogDraft = (patch) => {
    setLoopDialog((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        loop: {
          ...(previous.loop || {}),
          ...(patch || {}),
        },
      };
    });
  };

  const applyLoopDialog = () => {
    if (loopDialogWarnings.length > 0) {
      return { Success: false, Message: loopDialogWarnings.join(" ") };
    }

    const mode = loopDialog?.mode || "edit";
    const loopNumber = loopDialog?.loopNumber || getNextLoopNumber();
    const aliasNumber =
      getLoopNumberFromAlias(loopDialog?.loop) ||
      getLoopNumberFromAlias(loopDialogDraft) ||
      getNextLoopNumber();
    const loopDraft = loopDialog?.loop || {};
    const { suppressAutoTargetContainer, ...loopDraftForSave } = loopDraft;
    const allowAutoTargetContainer = suppressAutoTargetContainer !== true;
    const resolvedTargetContainerId = loopDialogUsesGroupedRows
      ? null
      : allowAutoTargetContainer
        ? autoTargetContainerId || (loopDialogShowsTargetContainer ? loopDraft?.targetContainerEntityObjectFieldId || defaultTargetContainerId : null)
        : null;
    const resolvedCollectionFieldId = loopDialogUsesGroupedRows ? null : loopDraft?.collectionSourceFieldId || null;
    const resolvedCollectionPath = loopDialogUsesGroupedRows
      ? ""
      : loopDraft?.collectionPath || (resolvedCollectionFieldId ? getCollectionPathFromFieldId(resolvedCollectionFieldId) : "");
    const nextLoop = {
      ...loopDraftForSave,
      name: String(loopDraft?.name || "").trim() || getLoopDefaultName(loopNumber, loopDialogSourceMode),
      loopAlias: String(loopDraft?.loopAlias || loopDialog?.loop?.loopAlias || `loop${aliasNumber}`).trim() || `loop${aliasNumber}`,
      entityObjectId: loopDraft?.entityObjectId || entityObject?.id,
      sourceObjectId: loopDraft?.sourceObjectId || activeSourceId,
      loopSourceMode: loopDialogSourceMode,
      groupBySourceFieldId: loopDialogUsesGroupedRows ? loopDraft?.groupBySourceFieldId || null : null,
      sortSourceFieldId: loopDialogUsesGroupedRows ? loopDraft?.sortSourceFieldId || null : null,
      targetMode: loopDialogUsesGroupedRows ? "FlatRows" : resolvedTargetContainerId ? "NestedContainer" : "FlatRows",
      targetContainerEntityObjectFieldId: resolvedTargetContainerId,
      collectionSourceFieldId: resolvedCollectionFieldId,
      collectionPath: resolvedCollectionPath,
      isActive: true,
      isDeleted: false,
    };
    setActiveLoops((previous) =>
      mode === "add"
        ? [...previous, nextLoop]
        : previous.map((loop) => (loop.id === nextLoop.id ? nextLoop : loop))
    );
    setActiveLoopId(nextLoop.id);
    setLoopDialog(null);
    scheduleAutoSave();
    return { Success: true };
  };

  const removeLoopFromDialog = () => {
    const removeId = loopDialog?.loop?.id;
    const existingLoop = removeId ? activeLoops.find((loop) => loop?.id === removeId) || null : null;
    if (existingLoop) {
      setDeleteLoopConfirm({
        ...existingLoop,
        loopNumber: loopDialog?.loopNumber || getLoopNumberFromAlias(existingLoop),
      });
    } else {
      setDeleteLoopConfirm(null);
    }
    setLoopDialog(null);
    return { Success: true };
  };

  const removeLoopById = (removeId) => {
    if (!removeId) return;
    setActiveLoops((previous) =>
      previous.map((loop) =>
        loop.id === removeId
          ? {
              ...loop,
              isActive: false,
              isDeleted: true,
            }
          : loop
      )
    );
    setActiveMap((previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) =>
        item?.loopMapId === removeId
          ? {
              ...item,
              isActive: false,
              isDeleted: false,
            }
          : item
      ),
    }));
    if (activeLoopId === removeId) setActiveLoopId(null);
    setSelectedEdgeId(null);
    setTransformEdgeId(null);
    setDeleteLoopConfirm(null);
    scheduleAutoSave();
  };

  useEffect(() => {
    if (transformEdgeId) setSelectedEdgeId(transformEdgeId);
  }, [transformEdgeId]);

  const moveEntityField = useCallback((dragId, targetId) => {
    if (!dragId || !targetId || dragId === targetId) return;
    setEntityFields((previous) => {
      const currentIndex = previous.findIndex((x) => x.id === dragId);
      const targetIndex = previous.findIndex((x) => x.id === targetId);
      if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) return previous;
      const next = previous.slice();
      const [moved] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  }, []);

  const startEntityReorder = (field, event, rowElement) => {
    if (isEntitySearchActive) return;
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", field.id);
      if (rowElement) event.dataTransfer.setDragImage(rowElement, 16, 16);
    }
    setReorderEntityFieldId(field.id);
  };

  const pressEntityReorder = (field) => {
    if (isEntitySearchActive) return;
    setReorderEntityFieldId(field.id);
  };

  const dragOverEntityRow = (targetField, event) => {
    if (isEntitySearchActive) return;
    event.preventDefault();
    if (event?.dataTransfer) event.dataTransfer.dropEffect = "move";
    const dragId = reorderEntityFieldId || event?.dataTransfer?.getData("text/plain");
    moveEntityField(dragId, targetField.id);
  };

  const dropOnEntityRow = (targetField, event) => {
    if (isEntitySearchActive) return;
    event.preventDefault();
    const dragId = reorderEntityFieldId || event?.dataTransfer?.getData("text/plain");
    if (!dragId || dragId === targetField.id) {
      setReorderEntityFieldId(null);
      return;
    }
    moveEntityField(dragId, targetField.id);
    setReorderEntityFieldId(null);
  };

  const endEntityReorder = () => {
    setReorderEntityFieldId(null);
  };

  const autoScrollWhileReordering = (event) => {
    if (!reorderEntityFieldId || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const pointerY = event.clientY - rect.top;
    const threshold = 56;
    const maxStep = 20;

    if (pointerY < threshold && canvas.scrollTop > 0) {
      const ratio = (threshold - Math.max(pointerY, 0)) / threshold;
      const step = Math.max(2, Math.round(maxStep * ratio));
      canvas.scrollTop = Math.max(0, canvas.scrollTop - step);
      return;
    }

    const distanceFromBottom = rect.height - pointerY;
    const maxScrollTop = canvas.scrollHeight - canvas.clientHeight;
    if (distanceFromBottom < threshold && canvas.scrollTop < maxScrollTop) {
      const ratio = (threshold - Math.max(distanceFromBottom, 0)) / threshold;
      const step = Math.max(2, Math.round(maxStep * ratio));
      canvas.scrollTop = Math.min(maxScrollTop, canvas.scrollTop + step);
    }
  };

  return (
    <div className="efmd">
      <div className="efmd-toolbar">
        <ButtonControl type={"cancel"} onClick={() => setShowRollbackConfirm(true)} disabled={!hasDraftAvailable}>
          Rollback
        </ButtonControl>
        <ButtonControl type={"ok"} onClick={saveNow} disabled={!hasUnsavedChanges}>
          Save
        </ButtonControl>
        <ButtonControl type={"cancel"} onClick={() => setShowClearConfirm(true)} disabled={!hasMappingsToClear}>
          Clear
        </ButtonControl>
        <div className="efmd-toolbar__spacer" />
        <button type="button" className="efmd-toolbar__icon-btn" title="Edit Entity Object" onClick={() => setEditEntityObjectOpen(true)}>
          <IconControl iconKey={"edit"} />
        </button>
        <div className={`efmd-scope-tag${activeLoop ? " is-loop" : ""}`} title={`Current mapping scope: ${activeScopeLabel}`}>
          <span className="efmd-scope-tag__text">
            <span className="efmd-scope-tag__label">Scope</span>
            <span className="efmd-scope-tag__value">{activeScopeLabel}</span>
          </span>
          {activeLoop && (
            <button
              type="button"
              className="efmd-scope-tag__edit"
              title={`Edit ${activeScopeLabel}`}
              onClick={() => openEditLoopDialog(activeLoop, activeLoopNumber)}
            >
              <IconControl iconKey={"edit"} />
            </button>
          )}
        </div>
        {activeLoop && (
          <div className="efmd-loop-mode-toggle" role="group" aria-label="Loop mapping mode">
            <button
              type="button"
              className={loopCanvasMode === "map" ? "is-active" : ""}
              onClick={() => setLoopCanvasMode("map")}
            >
              Map
            </button>
            <button
              type="button"
              className={loopCanvasMode === "aggregate" ? "is-active" : ""}
              onClick={() => setLoopCanvasMode("aggregate")}
            >
              Aggregate
            </button>
          </div>
        )}
        <div className="efmd-toolbar__menu-wrap">
          <button
            type="button"
            className={`efmd-toolbar__icon-btn${activeUtilityPanel || utilityMenuOpen ? " is-active" : ""}`}
            title="Mapping Panels"
            aria-haspopup="menu"
            aria-expanded={utilityMenuOpen}
            onClick={() => setUtilityMenuOpen((previous) => !previous)}
          >
            <IconControl iconKey={"viewmore"} />
          </button>
          {utilityMenuOpen && (
            <div className="efmd-toolbar-menu" role="menu" aria-label="Mapping panels">
              <button
                type="button"
                role="menuitem"
                className={showLoopsPanel ? "is-active" : ""}
                onClick={() => {
                  setActiveUtilityPanel((previous) => (previous === "loops" ? null : "loops"));
                  setUtilityMenuOpen(false);
                }}
              >
                Loops
              </button>
              <button
                type="button"
                role="menuitem"
                className={showArchivedMappings ? "is-active" : ""}
                onClick={() => {
                  setActiveUtilityPanel((previous) => (previous === "search" ? null : "search"));
                  setUtilityMenuOpen(false);
                }}
              >
                Search
              </button>
            </div>
          )}
        </div>
      </div>

      {validationSummaryText && <div className="efmd-errors">{validationSummaryText}.</div>}

      {showArchivedMappings && (
        <div className="efmd-toolbar efmd-toolbar--secondary">
          {allowMultiSource && (
            <div className="efmd-toolbar__search-field">
              <FieldControl
                className="efmd-toolbar__search-control efmd-toolbar__search-control--inline"
                fieldMetadata={buildSearchSelectMetadata({
                  fieldName: "searchMappingsSourceObjectId",
                  displayName: "Source Object",
                  dataJson: searchMappingSourceOptions,
                  placeHolder: "Select Source Object",
                })}
                value={selectedSearchSourceId || null}
                onChange={(value) => setSelectedSearchSourceId(value || "")}
              />
            </div>
          )}
          <div className="efmd-toolbar__search-field efmd-toolbar__search-field--wide">
            <FieldControl
              className="efmd-toolbar__search-control efmd-toolbar__search-control--inline"
              fieldMetadata={buildSearchSelectMetadata({
                fieldName: "searchMappingId",
                displayName: "Search Mappings",
                dataJson: searchMappingOptions,
                placeHolder: searchMappingOptions.length > 0 ? "Search Mappings" : "No Mappings",
              })}
              value={selectedSearchEdgeId || null}
              onChange={handleSelectSearchMapping}
              disabled={searchMappingOptions.length === 0}
            />
          </div>
          {selectedSearchMapping && isActiveEdge(selectedSearchMapping) ? (
            <button type="button" className="efmd-transform-btn" onClick={handleOpenSearchMapping}>
              Open
            </button>
          ) : (
            <ButtonControl type={"standard"} onClick={handleActivateSearchMapping} disabled={!selectedSearchMapping}>
              Make Active
            </ButtonControl>
          )}
        </div>
      )}

      {isLoopAggregateMode && (
        <div className="efmd-mode-hint">
          Aggregate mode: drag a loop source field or mapped loop entity field to a parent/summary entity field.
        </div>
      )}
      {showLoopsPanel && (
        <div className="efmd-loop-panel">
          <div className="efmd-loop-panel__top">
            <div className="efmd-loop-panel__title">Loops</div>
            <button type="button" className="efmd-transform-btn" onClick={openAddLoopDialog}>
              Add Loop
            </button>
          </div>

          <div className="efmd-loop-list" role="tablist" aria-label="Mapping scope">
            <button
              type="button"
              className={`efmd-loop-root-row${!activeLoopId ? " is-active" : ""}`}
              onClick={() => {
                setActiveLoopId(null);
                setLoopCanvasMode("map");
              }}
              role="tab"
              aria-selected={!activeLoopId}
            >
              <span className="efmd-loop-row__title">Root</span>
            </button>

            {activeVisibleLoops.length === 0 ? (
              <div className="efmd-loop-panel__hint">Add a loop to map repeated detail fields or calculate totals from detail rows.</div>
            ) : (
              activeVisibleLoops.map((loop, index) => {
                const loopNumber = loopDisplayNumberById.get(loop.id) || getLoopModeDisplayNumber(activeVisibleLoops, loop, index + 1);
                const label = getLoopDisplayName(loop, loopNumber);
                const summary = buildLoopSummary(loop, loopFieldOptions, entityFieldOptions);
                const loopValidation = loopValidationById.get(loop.id);
                const loopInvalid = loopValidation?.warnings?.length > 0;
                return (
                  <div
                    className={`efmd-loop-row${activeLoopId === loop.id ? " is-active" : ""}${loopInvalid ? " is-invalid" : ""}`}
                    key={loop.id}
                    role="tab"
                    aria-selected={activeLoopId === loop.id}
                    title={loopValidation?.warnings?.join(" ") || ""}
                  >
                    <button
                      type="button"
                      className="efmd-loop-row__main"
                      onClick={() => {
                        setActiveLoopId(loop.id);
                        setLoopCanvasMode("map");
                      }}
                    >
                      <span className="efmd-loop-row__title">{label}</span>
                      <span className="efmd-loop-row__summary">{summary || "Configure the loop source, target, and aggregate totals."}</span>
                    </button>
                    <div className="efmd-loop-row__actions">
                      <button type="button" className="efmd-transform-btn" onClick={() => openEditLoopDialog(loop, loopNumber)}>Edit</button>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--danger" onClick={() => setDeleteLoopConfirm({ ...loop, loopNumber })}>Delete</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      <div className="efmd-head">
        <div className="efmd-head__left">
          <div className="efmd-head__label">{sourceHeaderLabel}</div>
          {activeLoopSourceCollectionField ? (
            <div className="efmd-head__name-stack">
              <div className="efmd-head__name">{sourceHeaderName}</div>
              <div className="efmd-head__subname">{activeSource?.displayName}</div>
            </div>
          ) : allowMultiSource ? (
            <select value={activeSourceId} onChange={(event) => setActiveSourceId(event.target.value)} className="efmd-select">
              {asArray(sourceObjects).map((source) => (
                <option value={source.id} key={source.id}>
                  {source.displayName}
                </option>
              ))}
            </select>
          ) : (
            <div className="efmd-head__name">{sourceHeaderName}</div>
          )}
          <SearchInput value={searchSource} onChange={setSearchSource} />
        </div>
        <div className="efmd-head__right">
          <div className="efmd-head__entity-spacer" aria-hidden="true" />
          <div className="efmd-head__label">{entityHeaderLabel}</div>
          {activeLoopTargetCollectionField ? (
            <div className="efmd-head__name-stack">
              <div className="efmd-head__name">{entityHeaderName}</div>
              <div className="efmd-head__subname">{entityObjectDraftRaw?.DisplayName || entityObject?.displayName}</div>
            </div>
          ) : (
            <div className="efmd-head__name">{entityHeaderName}</div>
          )}
          <SearchInput value={searchEntity} onChange={setSearchEntity} />
        </div>
      </div>

      <div className="efmd-canvas" ref={canvasRef} onDragOver={autoScrollWhileReordering}>
        <div className="efmd-list efmd-list--source">
          {visibleSourceFields.map((field) => (
            <FieldRow
              key={field.id}
              side={"source"}
              field={field}
              isMapped={
                activeEdges.some((x) =>
                  x.sourceFieldId === field.id || asArray(x?.transformFields).some((input) => input?.sourceFieldId === field.id && input?.isDeleted !== true && input?.isActive !== false)
                ) ||
                activeAggregateEdges.some((x) =>
                  x.inputSourceFieldId === field.id || asArray(x?.transformFields).some((input) => input?.sourceFieldId === field.id && input?.isDeleted !== true && input?.isActive !== false)
                )
              }
              onStartConnect={beginSourceDrag}
              onQuickFilter={(clickedField) => {
                const term = String(clickedField?.fieldName || clickedField?.displayName || "").trim();
                setSearchSource((previous) => (String(previous || "").trim().toLowerCase() === term.toLowerCase() ? "" : term));
              }}
              onAiSuggestField={runAiForField}
              aiSuggestLoading={aiFieldLoadingTarget?.side === "source" && aiFieldLoadingTarget?.id === field.id}
              onEditEntityField={() => {}}
              setRef={(element) => {
                if (element) sourceRowRefs.current[field.id] = element;
                else delete sourceRowRefs.current[field.id];
              }}
            />
          ))}
        </div>
        <div className="efmd-gutter" ref={gutterRef} />
        <div className="efmd-list efmd-list--entity">
          {visibleEntityFields.map((field) => (
            <FieldRow
              key={field.id}
              side={"entity"}
              field={field}
              isMapped={
                activeEdges.some((x) => x.entityFieldId === field.id) ||
                activeAggregateEdges.some((x) => x.inputEntityFieldId === field.id || x.targetEntityFieldId === field.id)
              }
              onStartConnect={beginEntityDrag}
              onQuickFilter={(clickedField) => {
                const term = String(clickedField?.fieldName || clickedField?.displayName || "").trim();
                setSearchEntity((previous) => (String(previous || "").trim().toLowerCase() === term.toLowerCase() ? "" : term));
              }}
              onAiSuggestField={runAiForEntityField}
              aiSuggestLoading={aiFieldLoadingTarget?.side === "entity" && aiFieldLoadingTarget?.id === field.id}
              onEditEntityField={(field) =>
                setEditField({
                  ...field,
                  raw: {
                    ...(field.raw || {}),
                    _id: field?.raw?._id || field.id,
                    UniqueId: field?.raw?.UniqueId || field.id,
                    EntityObjectFieldId: field.id,
                    FieldName: field.fieldName,
                    DisplayName: field.displayName,
                    Description: field.description,
                    AIPromptContext: field.aiPromptContext,
                    Format: field.format,
                    DataTypeId: field.dataTypeId,
                    DataTypeName: field.dataTypeName,
                    IsActive: field.isActive,
                  },
                })
              }
              onStartReorder={startEntityReorder}
              onPressReorder={pressEntityReorder}
              onReleaseReorder={endEntityReorder}
              onDragOverRow={dragOverEntityRow}
              onDropOnRow={dropOnEntityRow}
              onEndReorder={endEntityReorder}
              isReordering={reorderEntityFieldId === field.id}
              disableReorder={isEntitySearchActive}
              setRef={(element) => {
                if (element) entityRowRefs.current[field.id] = element;
                else delete entityRowRefs.current[field.id];
              }}
            />
          ))}
        </div>

        <MappingOverlay
          edges={activeEdges}
          layout={layout}
          inputLayout={inputLayout}
          selectedEdgeId={selectedEdgeId}
          onSelectEdge={setSelectedEdgeId}
          onOpenTransform={setTransformEdgeId}
          onDeleteEdge={handleDeleteEdge}
          isEdgeInvalid={(edge) => edgeInvalid(edge, sourceFieldById, entityFieldById, validationLookupContext)}
          aggregateEdges={activeAggregateEdges}
          aggregateLayout={aggregateLayout}
          aggregateInputLayout={aggregateInputLayout}
          selectedAggregateId={selectedAggregateId}
          onSelectAggregate={setSelectedAggregateId}
          onOpenAggregate={openAggregateDialog}
          onDeleteAggregate={deleteAggregateById}
          loopEdges={activeLoopEdges}
          loopLayout={loopLayout}
          selectedLoopId={activeLoopId}
          onSelectLoop={(loopId) => {
            setActiveLoopId(loopId);
            setLoopCanvasMode("map");
          }}
          onOpenLoop={(loopId) => {
            const index = activeVisibleLoops.findIndex((loop) => loop?.id === loopId);
            if (index >= 0) {
              const loop = activeVisibleLoops[index];
              openEditLoopDialog(loop, loopDisplayNumberById.get(loop.id) || getLoopModeDisplayNumber(activeVisibleLoops, loop, index + 1));
            }
          }}
          draggingLine={dragging}
        />
      </div>
      <div className="efmd-footnote">
        <strong>Tips:</strong>{" "}
        {isLoopAggregateMode
          ? "Drag a source or mapped loop entity field to an entity total field to create a Sum aggregate. Click the sigma chip to edit reducer or formatting."
          : "Drag Source: Entity to map fields. Drag Entity: gutter (middle) to create a constant mapping. Double-click a mapping line to open its transform settings."}
      </div>

      <EntityFieldEditDialog
        open={!!editField}
        field={editField}
        dataTypes={dataTypes}
        fieldItemMetadata={fieldItemMetadata}
        onCancel={() => setEditField(null)}
        onSave={handleSaveEntityField}
      />

      <EntityObjectEditDialog
        open={editEntityObjectOpen}
        entityObject={{
          ...entityObject,
          raw: entityObjectDraftRaw || entityObject?.raw || {},
          displayName: entityObjectDraftRaw?.DisplayName || entityObject?.displayName,
        }}
        entityObjectItemMetadata={entityObjectItemMetadata}
        onCancel={() => setEditEntityObjectOpen(false)}
        onSave={async (patch) => {
          setEntityObjectDraftRaw((previous) => ({
            ...(previous || {}),
            ...(patch || {}),
            _id: previous?._id || entityObject?.id,
            EntityObjectId: previous?.EntityObjectId || entityObject?.id,
          }));
          setEditEntityObjectOpen(false);
          return { Success: true };
        }}
      />

      <TransformToolkitDialog
        open={!!transformEdgeId}
        mapping={selectedEdge}
        sourceField={sourceFieldById.get(selectedEdge?.sourceFieldId)}
        sourceFields={asArray(activeSource?.fields)}
        entityField={entityFieldById.get(selectedEdge?.entityFieldId)}
        sourceObjects={lookupSourceObjects}
        entityObjects={lookupEntityObjects}
        sourceObjectFieldMapItemMetadata={sourceObjectFieldMapItemMetadata}
        sourceObjectFieldTransformScriptItemMetadata={sourceObjectFieldTransformScriptItemMetadata}
        hideImportance={isSingleSourceOnly}
        hideInputs={true}
        transforms={transformCatalog}
        onCancel={() => setTransformEdgeId(null)}
        onDelete={() => {
          handleDeleteEdge(transformEdgeId);
          setTransformEdgeId(null);
        }}
        onSave={handleTransformSave}
        onAiBuildTransform={onAiBuildTransform}
      />

      <DialogControl
        openDialog={!!loopDialog}
        title={loopDialog?.mode === "edit" ? "Edit Loop" : "Add Loop"}
        className="efmd-loop-config-dialog"
        onX={() => setLoopDialog(null)}
        onCancel={() => setLoopDialog(null)}
        onOk={applyLoopDialog}
        cancelText={"Cancel"}
        okText={"Apply"}
        disableOk={loopDialogWarnings.length > 0}
        customCenterText={"Remove Loop"}
        customCenterType={"cancel"}
        onCustomCenter={removeLoopFromDialog}
      >
        <div className="efmd-dialog efmd-loop-dialog">
          <div className="efmd-loop-dialog-body">
            <div className="efmd-loop-dialog-grid">
                  {renderLoopFieldControl(
                    buildTextBoxMetadata({
                      fieldName: "name",
                      displayName: "Loop Display Name",
                      placeHolder: getLoopDefaultName(loopDialog?.loopNumber || activeLoopNumber, loopDialogSourceMode),
                    }),
                    loopDialogDraft?.name || "",
                    (value) => patchLoopDialogDraft({ name: value }),
                    "Short note shown in the designer. The runtime identifier is system-owned and hidden."
                  )}

                  {renderLoopFieldControl(
                    buildSelectListMetadata({
                      fieldName: "loopSourceMode",
                      displayName: "Source Mode",
                      dataJson: LOOP_SOURCE_MODES,
                    }),
                    loopDialogSourceMode,
                    (value) => {
                      const nextMode = value || "GroupedRows";
                      const autoCollectionField = collectionFieldOptions.length === 1 ? collectionFieldOptions[0] : null;
                      const currentCollectionFieldId = loopDialogDraft?.collectionSourceFieldId || autoCollectionField?.value || null;
                      patchLoopDialogDraft({
                        loopSourceMode: nextMode,
                        ...(nextMode === "GroupedRows"
                          ? { collectionSourceFieldId: null, collectionPath: "" }
                          : {
                              groupBySourceFieldId: null,
                              sortSourceFieldId: null,
                              collectionSourceFieldId: currentCollectionFieldId,
                              collectionPath: currentCollectionFieldId
                                ? getCollectionPathFromFieldId(currentCollectionFieldId)
                                : loopDialogDraft?.collectionPath || "",
                            }),
                      });
                    },
                    "Grouped Rows creates loop items by grouping rows. Inline Collection reads an array from one row.",
                    { disabled: loopDialogLocksCollectionSource, invalid: loopDialogInvalidFields.loopSourceMode }
                  )}

                  {renderLoopFieldControl(
                    buildSearchSelectMetadata({
                      fieldName: "groupBySourceFieldId",
                      displayName: "Group Key",
                      dataJson: loopFieldControlOptions,
                    }),
                    loopDialogUsesGroupedRows ? loopDialogDraft?.groupBySourceFieldId || "" : "",
                    (value) => patchLoopDialogDraft({ groupBySourceFieldId: value || null }),
                    loopDialogUsesGroupedRows
                      ? "Source field used to group flat source rows into parent records."
                      : "Inline Collection reads one collection from each row, so grouping is disabled.",
                    { disabled: !loopDialogUsesGroupedRows, invalid: loopDialogInvalidFields.groupBySourceFieldId }
                  )}

                  {loopDialogShowsCollectionPath && renderLoopFieldControl(
                    buildTextBoxMetadata({
                      fieldName: "collectionPath",
                      displayName: "Collection Path",
                      placeHolder: "items",
                    }),
                    loopDialogDraft?.collectionPath || "",
                    (value) => patchLoopDialogDraft({ collectionPath: value }),
                    "Advanced fallback dot path to the child array when the source schema does not expose a collection field.",
                    { invalid: loopDialogInvalidFields.collectionSourceFieldId }
                  )}

                  {renderLoopFieldControl(
                    buildSearchSelectMetadata({
                      fieldName: "collectionSourceFieldId",
                      displayName: "Collection Field",
                      dataJson: collectionFieldControlOptions,
                      placeHolder: null,
                    }),
                    loopDialogUsesGroupedRows ? "" : loopDialogDraft?.collectionSourceFieldId || "",
                    (value) => patchLoopDialogDraft({
                      collectionSourceFieldId: value || null,
                      collectionPath: value ? getCollectionPathFromFieldId(value) : "",
                    }),
                    loopDialogUsesGroupedRows
                      ? "Grouped Rows uses the Group Key, so collection selection is disabled."
                      : "Collection/array field to loop over. The collection path is set automatically.",
                    { disabled: loopDialogUsesGroupedRows || loopDialogLocksCollectionSource, invalid: loopDialogInvalidFields.collectionSourceFieldId }
                  )}

                  {renderLoopFieldControl(
                    buildSearchSelectMetadata({
                      fieldName: "sortSourceFieldId",
                      displayName: "Sort Key",
                      dataJson: loopFieldControlOptions,
                    }),
                    loopDialogUsesGroupedRows ? loopDialogDraft?.sortSourceFieldId || "" : "",
                    (value) => patchLoopDialogDraft({ sortSourceFieldId: value || null }),
                    loopDialogUsesGroupedRows
                      ? "Optional source field used to order loop rows before mapping them."
                      : "Inline Collection keeps the source collection order.",
                    { disabled: !loopDialogUsesGroupedRows, invalid: loopDialogInvalidFields.sortSourceFieldId }
                  )}

                  {loopDialogShowsTargetContainer && renderLoopFieldControl(
                    buildSearchSelectMetadata({
                      fieldName: "targetContainerEntityObjectFieldId",
                      displayName: "Target Container",
                      dataJson: targetContainerFieldControlOptions,
                      placeHolder: null,
                    }),
                    loopDialogDraft?.targetContainerEntityObjectFieldId || "",
                    (value) => patchLoopDialogDraft({ targetContainerEntityObjectFieldId: value || null }),
                    "Choose which child/list target receives the loop rows. This is hidden when there is zero or one possible target.",
                    { invalid: loopDialogInvalidFields.targetContainerEntityObjectFieldId }
                  )}

                  {renderLoopFieldControl(
                    buildCheckBoxMetadata({
                      fieldName: "hideWhenEmpty",
                      displayName: "Hide When Empty",
                    }),
                    loopDialogDraft?.emitParentWhenEmpty === false,
                    (value) => {
                      const hideWhenEmpty = value === true || String(value) === "true";
                      patchLoopDialogDraft({ emitParentWhenEmpty: !hideWhenEmpty });
                    },
                    "When checked, no parent/output record is created if this loop has no rows.",
                    { className: "efmd-loop-dialog-field--checkbox" }
                  )}
            </div>
            {loopDialogWarnings.length > 0 && (
              <div className="efmd-loop-dialog-warnings">
                {loopDialogWarnings.map((warning) => <div key={warning}>{warning}</div>)}
              </div>
            )}
          </div>
        </div>
      </DialogControl>

      <DialogControl
        openDialog={!!aggregateDialog}
        title={"Manage Aggregate"}
        className={`efmd-loop-config-dialog efmd-aggregate-config-dialog efmd-aggregate-config-dialog--${aggregateDialogTab}`}
        canFullScreen={true}
        onX={() => {
          setAggregateDialog(null);
          setAggregateDialogTab("settings");
        }}
        onCancel={() => {
          setAggregateDialog(null);
          setAggregateDialogTab("settings");
        }}
        onOk={applyAggregateDialog}
        cancelText={"Cancel"}
        okText={"Apply"}
        disableOk={aggregateDialogApplyWarnings.length > 0}
        customCenterText={"Delete Aggregate"}
        customCenterType={"cancel"}
        onCustomCenter={() => {
          deleteAggregateById(aggregateDialogDraft?.id, aggregateDialog?.loopId);
          setAggregateDialogTab("settings");
          return { Success: true };
        }}
      >
        <div className="efmd-dialog efmd-aggregate-dialog">
          <div className="efmd-loop-dialog-tabs efmd-aggregate-dialog-tabs" role="tablist" aria-label="Aggregate settings tabs">
            <button
              type="button"
              className={`efmd-loop-dialog-tab${aggregateDialogTab === "preTransform" ? " is-active" : ""}`}
              onClick={() => setAggregateDialogTab("preTransform")}
              role="tab"
              aria-selected={aggregateDialogTab === "preTransform"}
            >
              Pre Transform
            </button>
            <button
              type="button"
              className={`efmd-loop-dialog-tab${aggregateDialogTab === "settings" ? " is-active" : ""}`}
              onClick={() => setAggregateDialogTab("settings")}
              role="tab"
              aria-selected={aggregateDialogTab === "settings"}
            >
              Settings
            </button>
            <button
              type="button"
              className={`efmd-loop-dialog-tab${aggregateDialogTab === "transform" ? " is-active" : ""}`}
              onClick={() => setAggregateDialogTab("transform")}
              role="tab"
              aria-selected={aggregateDialogTab === "transform"}
            >
              Transform
            </button>
          </div>

          <div className={`efmd-aggregate-dialog-panel efmd-aggregate-dialog-panel--settings${aggregateDialogTab === "settings" ? "" : " is-hidden"}`}>
            <div className="efmd-aggregate-settings-context">
              <div className="efmd-transform-header">
                <div className="efmd-transform-header-field">
                  {renderHeaderFieldLabel(aggregateTransformResultField, "Transform Result")}
                </div>
                <div className="efmd-transform-arrow" title="Feeds transformed loop values into the reducer">{"->"}</div>
                <div className="efmd-transform-header-field">
                  {renderHeaderFieldLabel(aggregateReducerInputField, "Reducer Input")}
                </div>
              </div>
              {aggregateDialogInputSummary && (
                <div className="efmd-transform-inputs-hint efmd-transform-inputs-hint--drawn">
                  Inputs: {aggregateDialogInputSummary}
                </div>
              )}
            </div>
            <div className="efmd-aggregate-dialog-grid">
              <FieldControl
                className={`efmd-loop-dialog-control${aggregateDialogDraft && !LOOP_AGGREGATE_REDUCERS.some((option) => option?.Value === (aggregateDialogDraft?.reducer || "Sum")) ? " is-invalid" : ""}`}
                fieldMetadata={{
                  ...buildSelectListMetadata({
                    fieldName: "reducer",
                    displayName: "Reducer",
                    dataJson: aggregateReducerControlOptions,
                  }),
                  Description: "How loop values are combined before writing to the target field.",
                }}
                updatedItem={aggregateDialogDraft || {}}
                value={aggregateDialogDraft?.reducer || "Sum"}
                onChange={(value) => patchAggregateDialogDraft({ reducer: value || "Sum" })}
                disableValidation={true}
                disableError={true}
              />

              {(aggregateDialogDraft?.reducer || "Sum") === "Join" && (
                <FieldControl
                  className="efmd-loop-dialog-control"
                  fieldMetadata={{
                    ...buildTextBoxMetadata({
                      fieldName: "separator",
                      displayName: "Join Separator",
                      placeHolder: ",",
                    }),
                    Description: "Used only when the reducer is Join.",
                  }}
                  updatedItem={aggregateDialogDraft || {}}
                  value={aggregateDialogDraft?.separator || ","}
                  onChange={(value) => patchAggregateDialogDraft({ separator: value })}
                  disableValidation={true}
                  disableError={true}
                />
              )}
            </div>
            {aggregateDialogApplyWarnings.length > 0 && (
              <div className="efmd-loop-dialog-warnings">
                {aggregateDialogApplyWarnings.map((warning) => <div key={warning}>{warning}</div>)}
              </div>
            )}
          </div>

          <div className={`efmd-aggregate-dialog-panel${aggregateDialogTab === "preTransform" ? "" : " is-hidden"}`}>
            <TransformToolkitDialog
              embedded={true}
              showEditorTitle={true}
              open={!!aggregateDialog}
              editorRef={aggregatePreTransformEditorRef}
              mapping={aggregatePreTransformMapping}
              sourceField={aggregateDialogInputField}
              sourceFields={asArray(activeSource?.fields)}
              entityField={aggregateReducerInputField}
              sourceObjects={lookupSourceObjects}
              entityObjects={lookupEntityObjects}
              sourceObjectFieldMapItemMetadata={sourceObjectFieldMapItemMetadata}
              sourceObjectFieldTransformScriptItemMetadata={sourceObjectFieldTransformScriptItemMetadata}
              hideImportance={true}
              hideInputs={true}
              skipTypeMismatchWarning={true}
              titleText={"Pre Transform"}
              transforms={transformCatalog}
              onSave={() => {}}
              onDelete={() => {}}
              onValidationChange={(state) => updateAggregateEditorValidation("preTransform", state)}
              onAiBuildTransform={onAiBuildTransform}
            />
          </div>

          <div className={`efmd-aggregate-dialog-panel${aggregateDialogTab === "transform" ? "" : " is-hidden"}`}>
            <TransformToolkitDialog
              embedded={true}
              showEditorTitle={true}
              open={!!aggregateDialog}
              editorRef={aggregateTransformEditorRef}
              mapping={aggregatePostTransformMapping}
              sourceField={aggregateReducerResultField}
              sourceFields={asArray(activeSource?.fields)}
              entityField={aggregateDialogTargetField}
              sourceObjects={lookupSourceObjects}
              entityObjects={lookupEntityObjects}
              sourceObjectFieldMapItemMetadata={sourceObjectFieldMapItemMetadata}
              sourceObjectFieldTransformScriptItemMetadata={sourceObjectFieldTransformScriptItemMetadata}
              hideImportance={true}
              hideInputs={true}
              skipTypeMismatchWarning={true}
              titleText={"Transform"}
              transforms={transformCatalog}
              onSave={() => {}}
              onDelete={() => {}}
              onValidationChange={(state) => updateAggregateEditorValidation("transform", state)}
              onAiBuildTransform={onAiBuildTransform}
            />
          </div>

        </div>
      </DialogControl>

      <AiMappingConfirmDialog
        open={!!aiConfirm}
        summary={aiConfirm?.summary}
        warnings={aiConfirm?.warnings}
        canApprove={aiConfirm?.canApprove !== false}
        onCancel={() => setAiConfirm(null)}
        onConfirm={applyAiSuggestion}
      />

      <DialogControl
        openDialog={!!deleteLoopConfirm}
        title={"Delete Loop"}
        onX={() => setDeleteLoopConfirm(null)}
        onCancel={() => setDeleteLoopConfirm(null)}
        onOk={() => removeLoopById(deleteLoopConfirm?.id)}
        cancelText={"Cancel"}
        okText={"Delete"}
      >
        <div className="efmd-dialog">
          <div>
            Delete {getLoopDisplayName(deleteLoopConfirm, deleteLoopConfirm?.loopNumber || 1)} from this source object?
          </div>
          <div className="efmd-confirm-note">
            The loop is soft-deleted and {deleteLoopMappingCount} loop-scoped mapping{deleteLoopMappingCount === 1 ? "" : "s"} will be deactivated. Save afterward to persist the change.
          </div>
        </div>
      </DialogControl>

      <DialogControl
        openDialog={showClearConfirm}
        title={"Confirm Clear"}
        onX={() => setShowClearConfirm(false)}
        onCancel={() => setShowClearConfirm(false)}
        onOk={() => {
          clearCurrentMap();
          setShowClearConfirm(false);
        }}
        cancelText={"Cancel"}
        okText={"Clear"}
      >
        <div className="efmd-dialog">
          This will clear all mappings for the current source object from the designer. Save afterward to persist the removal.
        </div>
      </DialogControl>

      <DialogControl
        openDialog={showRollbackConfirm}
        title={"Confirm Rollback"}
        onX={() => setShowRollbackConfirm(false)}
        onCancel={() => setShowRollbackConfirm(false)}
        onOk={() => {
          rollback();
          setShowRollbackConfirm(false);
        }}
        cancelText={"Cancel"}
        okText={"Rollback"}
      >
        <div className="efmd-dialog">
          All unsaved changes will be lost, including field mappings, transform edits, and entity field updates. This action cannot be undone.
        </div>
      </DialogControl>
    </div>
  );
};

export default EntityFieldMapDesigner;
